import { Inject } from '@odinjs/odin';
import { SuggestionCardItemAPI } from '@philips-emr/patient-components/src';
import { AutocompleteAPI, Button, DatePickerAPI, DeckAPI, DynamicFieldsAPI, FetchAPI, FieldAPI, FormAPI, LoaderAPI, Metadata, ModalProps, ModalService, PanelAPI, SegmentedControlAPI, TextAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { ButtonTheme, Placement } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeFetch, OnChange, OnClick, OnDynamicContentRender, OnMount, OnNavigate, OnSubmit, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableFetchEvent, AbortableNavigateEvent, ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpResponse, HttpService, MediaType, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT, ISO_YEAR_MONTH_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';
import moment from 'moment/moment.js';
import React, { ComponentState } from 'react';
import { QuizItems } from 'src/interfaces/appointment';
import { getFoundationDataContext } from '../../app/bootstrap-foundation';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { EligibilityInsuranceResponse, ScheduleWaitingListData } from '../../app/types';
import { AppointmentsAliases } from '../../codegen';
import TokenConstants from '../../constants/TokenConstants';
import { DEFAULT_PATIENT_ID } from '../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { ToastResponse } from '../../interfaces/ToastResponse';
import Patient from '../../interfaces/patient';
import { AppointmentScheduleInfo, ScheduleItem, SuggestionGroups } from '../../interfaces/schedules-suggestions';
import AppointmentService from '../../services/appointment-service';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import AppointmentContext from '../../utils/appointment-context';
import { AppointmentUtils } from '../../utils/appointment-utils';
import { cast } from '../../utils/cast-helpers';
import { JWTTokenUtils } from '../../utils/jwt-token-utils';
import { NumberFormatUtils } from '../../utils/number-format-utils';
import { ObjectComparatorUtils } from '../../utils/object-comparator-utils';
import { defaultIfUndefined, removeEmptyProperties } from '../../utils/object-utils';
import { ParseHelperUtils } from '../../utils/parse-helper-utils';
import RescheduleContext from '../../utils/reschedule-utils';
import { showToastNotificationError, showToastNotificationMessage, showToastNotificationSuccess, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import './appointments-controller.css';
import { WaitingListData } from './scheduling-waiting-list/scheduling-waiting-list-controller';
import { REQUIREMENT_FIELDS, TeleconsultationType } from '../pseudonym-appointments/pseudonym-types';
import { EstablishmentGroupLocalizationModel, PseudonymCustomMenssage } from '../pseudonym-appointments/pseudonym-interfaces';
import { WizardAppointmentsIdentifies } from '../../constants/wizard-appointments-identifies';
import PrivateInsuranceConfiguration from '../../interfaces/private-insurance-configuration';

let defaultAllowedScheduling: DefaultAllowedScheduling[] = [];

interface DefaultAllowedScheduling {
  id: number,
  image: string,
  schedulingType: 'CONSULTATION' | 'EXAM',
  value: string
}

const DefaultQuizParams = {
  READ_ONLY: 'N',
  TRANSFER: 'N'
}

const Option = {
  NO: 'N',
  YES: 'S'
}

const NewScheduleTypes = {
  CONSULTATION: 'C',
  EXAM: 'E'
}

interface AllowedScheduling {
  scheduleAppointment: string;
  scheduleExam: string;
}

interface HealthInsurance {
  cdConvenio: string;
  dsConvenio: string;
  ieInsurancePrivate: string;
}

interface HealthInsurancePlan {
  cdCategoria: string;
  cdConvenio: number;
  cdPlano: string;
  dsPlano: string;
}

interface HealthInsuranceCategory {
  cdCategoria: string;
  cdConvenio: number;
  dsCategoria: string;
}

interface HealthInsuranceModalSubmitedProps {
  category?: HealthInsuranceCategory;
  healthCardNumber?: string;
  insurance: HealthInsurance;
  plan?: HealthInsurancePlan;
  saveInsurance: boolean;
  walletExpiryDate?: string;
}

interface SpecialityData {
  cdEspecialidade: string;
  dsEspecialidade: string;
}

interface MedicalArea {
  dsAreaAtuacao: string;
  nrSequencia: number;
}

interface Physician {
  cdPessoaFisica: string;
  nmPessoaFisica: string;
}

interface ExamSchedule {
  dsImagemBase64: string;
  dsProcInterno: string;
  dsTipoExame: string;
  nrSeqProcInterno: string;
}

interface ConsultFieldItem {
  medicalArea?: MedicalArea;
  physician?: Physician;
  specialityData?: SpecialityData;
}

interface ExamFieldItem {
  exam: ExamSchedule;
}

interface LocationData {
  dsGrupoEstab: string;
  nrSequencia: string;
}

interface FormStep02Props {
  grupoEstab?: LocationData;
}

interface DateRange {
  end: string;
  start: string;
}

interface NewScheduleFields extends Record<string, Value>{
  consultation: ConsultFieldItem[];
  dataRage: DateRange;
  exam: ExamSchedule[];
  insurance: {
    autorizationCode: string;
    category: {
      cdCategoria: string;
      dsCategoria: string;
    };
    convenio: {
      cdConvenio: string;
      dsConvenio: string;
    };
    hasInsurance: boolean;
    healthCardNumber?: string;
    healthCardValidity?: string;
    plan: {
      cdPlano: string;
      dsPlano: string;
    };
  };
  location: {
    id: string;
    value: string;
  };
  patient: {
    nmPessoaFisica: string;
    patientId: string;
  };
  scheduleType: [{
    id: string;
    value: string;
  }];
}

interface SegmentedControlProps {
  image: string;
  value: string;
}

interface FieldSpecialityProps {
  cdEspecialidade: number;
  dsEspecialidade: string;
}

interface FieldPhysicianProps {
  cdPessoaFisica: string;
  dsEspecialidade: string;
  dsInfoMedico: string;
  dsTipoAgendamento: string;
  dsTipoAgendamentoDsEspecialidade: string;
  fotoMedicoBase64: string;
  nmPessoaFisica: string;
}
interface SchedulingConfig {
  allowSelectDesiredDate: boolean;
  desiredDateCustomMessage: string;
  scheduleRangeAllowed: number;
  schedulingAllowedDays: number;
  schedulingFinalDate: Date;
}

interface DefaultComponentTarget {
  state: {
    alias: string
  }
}

interface ClearTimeReservation {
  nrSeqAgeintItem: number;
}

interface PhysicianAndSpeciality {
  cdEspecialidade: number;
  cdMedico: string;
}

interface HealthInsuranceCardProps {
  cardId: string;
  categoryDesc?: string;
  categoryId?: string;
  healthCardNumber?: string;
  healthInsuranceDesc: string;
  healthInsuranceId: number;
  isNewItemButton?: boolean;
  isPrivate?: boolean;
  planDesc?: string;
  planId?: string;
  walletExpiryDate?: string;
}

interface FormInsuranceRecordProps {
  autorizationCode: string;
  itemSelected: HealthInsuranceCardProps[];
}

interface HealthInsurancesReduced {
  insurancesCardButtons: HealthInsuranceCardProps[];
  insurancesCards: HealthInsuranceCardProps[];
}

interface WaitingListDataReduced {
  itemRemoved?: ScheduleWaitingListData;
  newData: ScheduleWaitingListData[];
}

interface HealthInsuranceHolderDTO {
  cdCategoria: string;
  cdConvenio: number;
  cdPlanoConvenio: string;
  cdUsuarioConvenio: string;
  dtFimVigencia: string;
  dtInicioVigencia: string;
  dtValidadeCarteira: string;
}

interface EligibilityDTO {
  beneficiary?: string;
  cardExpirationDate?: string;
  categoryCode?: string;
  insuranceCode?: number;
  naturalPersonName?: string;
  planCode?: string;
}

interface CurrentPatientSelected {
  id: number;
  name: string;
}

interface EligibilityResponseError {
  message: string;
  proceedIfHasError: boolean;
}

interface ExamScheduleProps {
  exam: {
    dsImagemBase64: string;
    dsProcInterno: string;
    dsTipoExame: string;
    nrSeqProcInterno: number;
  }
}

interface SpecialityDataProps {
  cdEspecialidade?: number;
}

interface ConsultScheduleProps {
  physician?: PhysicianProps
  specialityData?: SpecialityDataProps;
}

interface PhysicianProps {
  cdPessoaFisica?: string;
  nmPessoaFisica?: string;
}

interface ScheduleTypeProps {
  id: number;
  value: string;
}

interface WaitingListDataFromCardProps {
  consultScheduleList: ConsultScheduleProps[];
  examScheduleList: ExamScheduleProps[];
  nrSeqAgeint: number;
  physician?: PhysicianProps;
  scheduleType: ScheduleTypeProps[];
}

interface AvailableCalendarDaysProps {
  availableDays: string[];
  endAt?: string;
  existsAnyBlockedItems: boolean;
  nrSeqAgeint: number;
  nrSeqAgeintItem: string;
  startAt?: string;
}

interface AdditionalInformation {
  heightInCentimeters: number;
  saveAdditionalInfo: boolean;
  weight: number;
}

interface ParamsSeqAgenda {
  cdCategoria: string;
  cdConvenio: string;
  cdPlano: string;
  healthCardNumber: string;
  healthCardValidity: string;
  locationId: string;
  nrSeqAgeint: number,
  patientHeight?: number;
  patientId: string;
  patientWeight?: number,
}

interface FieldTypes {
  dsValorNew: Value;
  dsValorOld: Value;
  nmAtributo: string;
}

interface SimpleObjectResponse {
  value: string;
}

interface IntegratedAgendaSequenceResponse {
  value: number;
}

interface TimeReservationResponse {
  abortMessage?: string;
}

interface QuizAnswer {
  [key: string]: string | boolean | number | QuizAnswer[] | undefined;
  label: string;
  value: string;
}

interface CustomMetadata extends Metadata {
  answer: string;
  children: CustomMetadata[];
  sequenceAnswer: string;
  visible: boolean;
}

@Controller({ component: 'layout-appointments', fragment: 'appointments' })
export default class AppointmentsController extends BaseController {

  private dynamicMetada!: CustomMetadata;
  private suggestionList: ScheduleItem[] = [];

  private specialityFields: FieldAPI[] = [];
  private specialityFetches: FetchAPI[] = [];
  private physicianFields: FieldAPI[] = [];
  private physicianFetches: FetchAPI[] = [];
  private medicalAreaFields: FieldAPI[] = [];
  private medicalAreaFetches: FetchAPI[] = [];

  private haveLocation = true;
  private nrSeqAgeint!: number;
  private newScheduleFields!: NewScheduleFields;
  private newScheduleFieldsOldValues!: NewScheduleFields;
  private selectedDateRange!: DateRange;
  private cdConvenioParticular!: string;
  private privateCategoryCode!: string;

  private isReschedule!: boolean;
  private isStepQuizDone = true;
  private isQuizAvailable!: boolean;

  private HealthInsuranceCardDefaultIds = {
    newItemButtonId: '#ADD_NEW_BUTTON',
    privateButtonId: '#PRIVATE',
  }

  private newInsuranceSelectedAfterCheckEligibility?: HealthInsuranceCardProps;
  private suggestionsAfterGetData?: ScheduleItem[];
  private blockedSuggestionsAfterGetData?: ScheduleItem[];
  private wizardIdentifierToBeDoneWhenInsuranceFieldsFilled?: string;

  @Inject('panel-info')
    panelInfo!: PanelAPI;

  @Inject('fetch-specility')
    specialityFetch!: FetchAPI;

  @Inject('fetch-medical-area')
    medicalAreaFetch!: FetchAPI;

  @Inject('fetch-field-physician')
    physicianFetch!: FetchAPI;

  @Inject('wizard-appointments')
    wizard!: WizardAPI;

  @Inject('field-medical-area')
    fieldMedicalArea!: FieldAPI;

  @Inject('field-speciality')
    fieldSpeciality!: FieldAPI;

  @Inject('field-physician')
    fieldPhysician!: FieldAPI;

  @Inject('segmented-control-schedule-type')
    staticSegmentedControlAPI!: SegmentedControlAPI;

  @Inject('segmented-control-schedule-type-result')
    staticSegmentedControlResultAPI!: SegmentedControlAPI;

  @Inject('segmented-control-0')
    segmentedControlInsurance!: SegmentedControlAPI;

  @Inject('segmented-control-0-result')
    segmentedControlInsuranceResult!: SegmentedControlAPI;

  @Inject('field-exams-to-schedule')
    fieldExamToSchedule!: FieldAPI;

  @Inject('deck-choosen-items')
    deckChoosenItems!: DeckAPI;

  @Inject('deck-blocked-items')
    deckBlockedItemsAPI!: DeckAPI;

  @Inject('text-total')
    totalText!: TextAPI;

  @Inject('autocomplete-step-1')
    patientAutocomplete!: AutocompleteAPI;

  @Inject('form-insurance')
    formInsurance!: FormAPI;

  @Inject('form-patient')
    formPatient!: FormAPI;

  @Inject('field-scheduling-list')
    fieldSchedulingList!: FieldAPI;

  @Inject('autocomplete-location')
    locationAutocomplete!: AutocompleteAPI;

  @Inject('step-2-fetch')
    fetchLocationList!: FetchAPI;

  @Inject('panel-loader')
    panelLoader!: PanelAPI;

  @Inject('panel-deck')
    panelDeck!: PanelAPI;

  @Inject('panel-blocked-items')
    panelBlockedItemsAPI!: PanelAPI;

  @Inject('date-picker-appointment-range')
    datePickerAppointmentRange!: DatePickerAPI;

  @Inject('panel-total')
    panelTotalAPI!: PanelAPI;

  @Inject('panel-loader-eligibility')
    panelLoaderEligibilityAPI!: PanelAPI

  @Inject('form-schedule-type')
    formScheduleTypeAPI!: FormAPI;

  @Inject('step-2-form')
    step2FormAPI!: FormAPI;

  @Inject('form-step-3')
    formStep3API!: FormAPI;

  @Inject('form-exams-to-schedule')
    formExamsToScheduleAPI!: FormAPI;

  @Inject('loader-final')
    loaderFinalAPI!: LoaderAPI;

  @Inject('panel-notification-step-date')
    panelNotificationStepDateAPI!: PanelAPI;

  @Inject('panel-notification-step-suggestion')
    panelNotificationStepSuggestionAPI!: PanelAPI;

  @Inject('panel-schedule-type')
    panelScheduleTypeAPI!: PanelAPI;

  @Inject('form-additional-information')
    formAdditionalInformationAPI!: FormAPI;

  @Inject('fetch-additional-information')
    fetchAdditionalInformationAPI!: FetchAPI;

  @Inject('field-checkbox-save')
    fieldCheckboxSaveAPI!: FieldAPI;

  @Inject(AppointmentsAliases.DYNAMIC_FIELDS_QUIZ)
    quizDynamicFieldsAPI!: DynamicFieldsAPI;

  @Inject(AppointmentsAliases.FORM_QUIZ)
    quizFormAPI!: FormAPI;

  @Inject(AppointmentsAliases.STEP_2_FIELD)
    fieldLocationAPI!: FieldAPI;

  state = {
    blockedCustomMessage: '',
    finalDateInitialRange: '',
    today: '',
  };

  @OnChange({target: AppointmentsAliases.FORM_QUIZ})
  onChangeDynamicFields(): void {
    const answerFormQuiz: Record<string, Value> = this.quizFormAPI.getRecord();

    Object.entries(answerFormQuiz).forEach(([key, value]) => {
      const answer = this.formatQuizAnswer(value);
      this.updateAnswerStepQuiz(key, answer, this.dynamicMetada.id, this.dynamicMetada.children);
    })

    this.isStepQuizDone = true;
    this.checkRequiredFieldsStepQuiz(this.dynamicMetada.children);
    this.wizard.setStepDone('quiz', this.isStepQuizDone);
  }

  formatQuizAnswer(value: Value): QuizAnswer{
    const answer: QuizAnswer = {label: '', value : ''};

    if(ObjectComparatorUtils.isObject(value)){
      return value as QuizAnswer;
    }

    answer.label = value as string;

    return answer;
  }

  updateAnswerStepQuiz(key: string, answer: QuizAnswer, fatherId: string, children: CustomMetadata[]): void{
    if(children == null || children?.length === 0){
      return;
    }

    children.forEach((value) => {
      if(value.id === key && value.answer != answer.label){

        value.answer = answer.label;
        value.sequenceAnswer = answer.value;

        this.saveQuizItem(fatherId, value);
      }

      this.updateAnswerStepQuiz(key, answer, value.id, value.children);
    });
  }

  saveQuizItem(seqAgeintItem: string, quizItem: CustomMetadata): void{
    const body = {
      answer: quizItem.answer,
      sequenceAnswer: quizItem.sequenceAnswer !== '' ? Number(quizItem.sequenceAnswer) : null,
      sequenceIntegratedSchedule: Number(this.dynamicMetada.id),
      sequenceItemIntegratedSchedule: Number(seqAgeintItem),
      sequenceStructureQuestion: Number(quizItem.id)
    };

    HttpService.put(APPLICATION_ENDPOINTS.PUT_QUIZ_ITEM, {nrSeqAgeint: Number(this.dynamicMetada.id)}, { body})
      .then((response) => {
        const quizItems = response.body as QuizItems[];
        this.setQuizItemVisibility(quizItems, this.dynamicMetada.children);
        this.quizDynamicFieldsAPI.setMetadata(this.dynamicMetada as Metadata);
      });
  }

  private setQuizItemVisibility = (quizItems: QuizItems[], children: CustomMetadata[]): void => {
    if(!quizItems || children == null || children?.length === 0){
      return;
    }

    children.forEach((value) => {
      const entryQuizItem = value.entry != null ? value.entry : '';

      for(let indexQuizItems = 0; indexQuizItems < quizItems.length; indexQuizItems++) {
        const sequenceStructure = quizItems[indexQuizItems].sequenceStructure != null ? quizItems[indexQuizItems].sequenceStructure?.toString() : '';

        if(entryQuizItem != null && sequenceStructure === entryQuizItem && quizItems[indexQuizItems]?.visible != null){
          value.visibilityCondition = this.getQuizItemVisible(quizItems[indexQuizItems].visible);
        }
      }

      this.setQuizItemVisibility(quizItems, value.children);
    });
  }

  private getQuizItemVisible = (visible: string | undefined): string => {
    return visible ? visible : Option.NO;
  }

  checkRequiredFieldsStepQuiz(children: CustomMetadata[]): void {
    if(!this.isStepQuizDone || children == null || children?.length === 0){
      return;
    }

    children.forEach(value => {
      if (value.required && (value.answer == null)){
        this.isStepQuizDone = false;
      }

      this.checkRequiredFieldsStepQuiz(value.children);
    });
  }

  @OnMount({ target: 'layout-appointments'})
  onLayoutAppointmentsMount(): void {
    const event = new CustomEvent('updateModulesRoute', { detail: { moduleId: ApplicationModulesIds.APPOINTMENTS } });
    dispatchEvent(event);

    this.isReschedule = this.historyService.getCurrentRoute().includes(APPLICATION_ROUTES.RESCHEDULE_APPOINTMENTS);
    if(this.isReschedule) {
      this.wizard.setHiddenSteps(['schedule-type', 'scheduling-for', 'consultation', 'exams', 'exam-additional-information', 'quiz', 'location']);
      this.getHealthInsuranceIdToContext();
    }
  }

  private getHealthInsuranceIdToContext(): void {
    HttpService.get<number>(
      APPLICATION_ENDPOINTS.HEALTH_INSURANCE_BY_NR_SEQ_AGEINT,
      {nrSeqAgeint: RescheduleContext.nrSeqAgeint, patientId: UserContext.patientId}
    ).then(healthInsuranceResponse => {
      this.context.set({healthInsuranceIdToReschedule: healthInsuranceResponse.body});
      this.setHealthInsurancePrivate().then(response => this.cdConvenioParticular = response.insuranceCode.toString());
    }).catch(() => {
      this.callToastLoadDataError();
    });
  }

  @OnMount(AppointmentsAliases.DECK_CHOOSEN_ITEMS)
  onMountDeckChoosenItems(event: MountEvent): void {
    const deckApi = event.target as DeckAPI;
    this.setSuggestionsOrBlockedSuggestionsIfExists(deckApi, this.suggestionsAfterGetData);
  }

  @OnMount(AppointmentsAliases.DECK_BLOCKED_ITEMS)
  onMountDeckBlockedItems(event: MountEvent): void {
    const deckApi = event.target as DeckAPI;
    this.setSuggestionsOrBlockedSuggestionsIfExists(deckApi, this.blockedSuggestionsAfterGetData);
  }

  private setSuggestionsOrBlockedSuggestionsIfExists(deckApi: DeckAPI, suggestions?: ScheduleItem[]): void {
    if (suggestions) {
      deckApi.setData(cast(suggestions));
      deckApi.setDone(true);
      suggestions = undefined;
    }
  }

  @OnUnmount({target: 'layout-appointments'})
  handleUnMountLayout(): void {
    ToastService.closeAll();
  }

  @OnMount({ target: 'panel-insurance' })
  onMountInsurancePanel(): void {
    if (this.isReschedule && this.haveLocation) {
      this.wizard.setHiddenSteps(['scheduling-for', 'consultation', 'exams', 'exam-additional-information']);
    }
  }

  @OnMount(AppointmentsAliases.PANEL_INFO)
  handleOnLoadingPanelInfo(event: MountEvent): void {
    const panelApi = event.target as PanelAPI;
    panelApi.setVisible(false);
  }

  @OnMount({ target: 'wizard-step-insurance' })
  onMountStepScheduleFor(event: MountEvent): void {
    if(event) {
      this.selectPatientIfHaveOnlyOne();
    }
  }

  private buildAllowedScheduling(allowedScheduling: AllowedScheduling): void {
    defaultAllowedScheduling = [];
    if (allowedScheduling.scheduleAppointment === 'S') {
      defaultAllowedScheduling.push({
        id: 1,
        image: '/static/assets/avatars/consultation.svg',
        schedulingType: 'CONSULTATION',
        value: 'schedule-type.consultation',
      });
    }
    if (allowedScheduling.scheduleExam === 'S') {
      defaultAllowedScheduling.push({
        id: 2,
        image: '/static/assets/avatars/exams.svg',
        schedulingType: 'EXAM',
        value: 'schedule-type.exam',
      });
    }
  }

  @OnMount({ target: ['wizard-appointments'] })
  onMountStepLocation(event: MountEvent): void {
    if (event) {
      setTimeout(() => {
        this.showLocationStep(0);
        this.showQuizStep();
      });
    }
  }

  @OnChange({ target: [AppointmentsAliases.AUTOCOMPLETE_STEP_1] })
  async onChangePatient(event: ChangeEvent<Patient>): Promise<void> {
    if(!event.value) {
      this.panelInfo.setVisible(false);
      this.context.set({
        currentPatientSelected: undefined,
        isAlreadyHealthInsurance: false,
      });
      return;
    }
    this.panelInfo.setVisible(true);
    if(this.context.get('isAlreadyHealthInsurance') && event.value.cdPessoaFisica) {
      this.loadPatientHealthInsurancesAndShow(Number(event.value.cdPessoaFisica));
    }
    setTimeout(() => {
      this.staticSegmentedControlAPI.setData(cast([...defaultAllowedScheduling]));
    });
    const currentPatientSelected: CurrentPatientSelected = {
      id: Number(defaultIfUndefined(event.value.cdPessoaFisica, DEFAULT_PATIENT_ID)),
      name: event.value.nmPessoaFisica,
    }

    this.clearMedicalFields();

    this.context.set({ additionalInformationAlreadyExecuted: false, currentPatientSelected });
  }

  @OnMount({target: 'panel-loader-schedule-type'})
  handlePanelLoaderSchedueType(event: MountEvent): void {
    if (!event) {
      return;
    }

    const panelLoaderAPI = event.target as PanelAPI;
    HttpService.post(APPLICATION_ENDPOINTS.ALLOWED_SCHEDULING, {}).then(response => {
      const allowedScheduling = response.body as AllowedScheduling;
      this.buildAllowedScheduling(allowedScheduling);
      panelLoaderAPI.setVisible(false);
      this.panelScheduleTypeAPI.setVisible(true);
    });

    this.getTasyParams();
  }
  @OnMount({target: 'segmented-control-schedule-type'})
  handleMountSegmentedControlSchedule(event: MountEvent): void {
    const segmentedControlSchedule = event.target as SegmentedControlAPI;
    segmentedControlSchedule.setData(cast([...defaultAllowedScheduling]));
  }

  @OnChange({ target: ['segmented-control-schedule-type'] })
  onChangeRequiredFields(event: ChangeEvent<DefaultAllowedScheduling[]>): void {
    if (event) {
      const component = event.target as unknown as DefaultComponentTarget;
      switch (component.state.alias) {
        case 'segmented-control-schedule-type':
          this.onChangeSeg(event);
          break;
        default:
          break;
      }
      this.checkStepOne(event.value);
    }
  }

  @OnChange({ target: 'date-picker-appointment-range' })
  onChangeDateRange(event: ChangeEvent<string[]>): void {
    if (event && event.value) {
      let rangeDate = event.value || [];
      if(rangeDate.length === 1) {
        rangeDate = [rangeDate[0], rangeDate[0]];
      }
      if (rangeDate.length > 1) {
        const rangeDateArray = rangeDate as unknown as [string, string];
        this.selectedDateRange = this.selectedDateRange || {}
        this.selectedDateRange.start = rangeDateArray[0] > rangeDateArray[1] ? rangeDateArray[1] : rangeDateArray[0];
        this.selectedDateRange.end = rangeDateArray[0] > rangeDateArray[1] ? rangeDateArray[0] : rangeDateArray[1];
        this.wizard.setStepDone('range-date', true);
      } else {
        this.wizard.setStepDone('range-date', false);
      }
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['field-speciality'] })
  onMountFieldSpeciality(evt: MountEvent): void {
    const target: FieldAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);
    this.specialityFields[componentState.identifier] = target;
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['fetch-specility']})
  onMountFetchSpecility(evt: MountEvent): void {
    const target: FetchAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);
    this.specialityFetches[componentState.identifier] = target;
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnChange({ target:  'field-speciality'})
  onChangeFieldSpeciality(evt: ChangeEvent<AutocompleteAPI>): void {
    const target: FieldAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);
    this.physicianFields[componentState.identifier].setValue({});
    this.physicianFetches[componentState.identifier].forceFetch();
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['field-medical-area']})
  onMountFieldMedicalArea(evt: MountEvent): void {
    const target: FieldAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);
    this.medicalAreaFields[componentState.identifier] = target;
    const fieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');
    target.setVisible(!!fieldMedicalAreaIsVisible);
  }

  @OnMount({target: 'field-list-scheduling'})
  handleFieldListSchedulingLoading(): void {
    this.getShowMedicalAreaValueFromTasy().then(() => {
      const fieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');

      this.medicalAreaFields.forEach(medicalField => {
        medicalField.setVisible(!!fieldMedicalAreaIsVisible);
      });
    });
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['field-physician'] })
  onMountFieldPhysician(evt: MountEvent): void {
    const target: FieldAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);
    this.physicianFields[componentState.identifier] = target;
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['fetch-field-physician']})
  onMountFetchPhysician(evt: MountEvent): void {
    const target: FetchAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);

    target.setLazy(true);
    target.setEndpoint(APPLICATION_ENDPOINTS.GET_PHYSCIAN);
    this.physicianFetches[componentState.identifier] = target;
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['fetch-medical-area']})
  onMountFetchMedicalArea(evt: MountEvent): void {
    const target: FetchAPI = cast(evt.target);

    //@ts-ignore
    const componentState: ComponentState = cast(target.state);

    this.medicalAreaFetches[componentState.identifier] = target;
    const fieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');

    if (fieldMedicalAreaIsVisible) {
      this.medicalAreaFields[componentState.identifier].setVisible(!!fieldMedicalAreaIsVisible);
      target.setParameters({dsAreaAtuacao: ''});
      target.setEndpoint(APPLICATION_ENDPOINTS.NEW_APPOINTMENT_MEDICAL_AREA_LIST);
      target.forceFetch();
    }
  }

  @OnMount({ target: ['date-picker-appointment-range'] })
  onMountDatePicker(): void {
    HttpService.post(APPLICATION_ENDPOINTS.GET_NEW_APPOINTMENT_SCHEDULE_CONFIG).then(response  => {
      const today = moment().utc().toISOString();
      const schedulingConfig = response.body as SchedulingConfig;
      this.datePickerAppointmentRange.setIntervalThreshold(schedulingConfig.scheduleRangeAllowed);
      const finalDate = schedulingConfig.schedulingFinalDate.toString();
      this.datePickerAppointmentRange.addRange(today, finalDate);

      this.context.set({
        allowSelectDesiredDate: schedulingConfig.allowSelectDesiredDate,
        desiredDateCustomMessage: schedulingConfig.desiredDateCustomMessage
      });

      this.state = {
        blockedCustomMessage: this.state.blockedCustomMessage,
        finalDateInitialRange: finalDate,
        today: today
      };

      if(this.isReschedule && !this.haveLocation) {
        const nrSeqAgeint = RescheduleContext.nrSeqAgeint;
        HttpService.get(
          APPLICATION_ENDPOINTS.CONSULTATION_PHYSICIAN_AND_SPECIALITY, { nrSeqAgeint, patientId: UserContext.patientId })
          .then(res => {
            const data = res.body as PhysicianAndSpeciality;

            if(data.cdMedico && data.cdEspecialidade) {
              this.setAvailableCalendarDaysForReschedule(data, nrSeqAgeint);
            }
          });
      }
    });
  }

  restoreInitialRangeDatePicker(): void {
    this.datePickerAppointmentRange.clearRanges();
    this.datePickerAppointmentRange.addRange(this.state.today, this.state.finalDateInitialRange);
  }

  selectPatientIfHaveOnlyOne(): void {
    const currentPatientId = UserContext.patientId;
    HttpService.get(APPLICATION_ENDPOINTS.NEW_APPOINTMENT_PATIENT, { patientId: currentPatientId }).then(response => {
      const currentPatientSelected = {} as CurrentPatientSelected;
      const patientList = defaultIfUndefined(response.body, []) as Patient[];

      if (!patientList.length) {
        throw Error('No patient found');
      }

      if (patientList.length === 1) {
        const [patient] = patientList;
        currentPatientSelected.id = Number(defaultIfUndefined(patient.cdPessoaFisica, DEFAULT_PATIENT_ID));
        currentPatientSelected.name = patient.nmPessoaFisica;
      } else {
        const patientSelected = patientList.find(patient => patient.cdPessoaFisica === currentPatientId.toString());
        if (!patientSelected) {
          throw Error(`No patients found with ${currentPatientId} code`);
        }

        currentPatientSelected.id = currentPatientId;
        currentPatientSelected.name = patientSelected.nmPessoaFisica;
      }

      this.formPatient.setData({
        nmPessoaFisica: { cdPessoaFisica: currentPatientSelected.id, nmPessoaFisica: currentPatientSelected.name }
      });

      this.panelInfo.setVisible(false);
      this.panelInfo.setVisible(true);
      this.context.set({ additionalInformationAlreadyExecuted: false, currentPatientSelected });
      setTimeout(() => {
        this.staticSegmentedControlAPI.setData(cast([...defaultAllowedScheduling]));
      });
    })
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnAfterFetch({ target: ['fetch-medical-area']})
  onAfterFetchMedicalArea(evt: FetchEvent): void {
    const result = evt.data as [];
    const fieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');
    if ((result.length == 0) || (!fieldMedicalAreaIsVisible)) {
      const target: FetchAPI = cast(evt.target);

      //@ts-ignore
      const componentState: ComponentState = cast(target.state);

      this.medicalAreaFields[componentState.identifier].setVisible(false);
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnBeforeFetch({ target: ['fetch-specility']})
  onBeforeFetchSpeciality(evt: FetchEvent): void {
    const target: FetchAPI = cast(evt.target);

    //@ts-ignore
    const state: ComponentState = cast(target.state);

    const dontHasInsurance = Boolean(this.context.get('isPrivateHealthInsurance'))
    const cdConvenio = dontHasInsurance
      ? ''
      : this.getInsuranceCardSelectedData()?.healthInsuranceId || '';

    let locationNrSeq = '0';
    if(!this.wizard.getHiddenSteps().includes('location')
    && this.locationAutocomplete != null
    && this.locationAutocomplete.getValue()) {
      const locationSelected = this.locationAutocomplete.getValue() as unknown as LocationData;
      locationNrSeq = locationSelected.nrSequencia;
    }

    this.specialityFetches[state.identifier].setParameters({
      cdConvenio,
      ieParticular: dontHasInsurance,
      nrSeqLocal: locationNrSeq,
    });
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnBeforeFetch({ target: ['fetch-field-physician']})
  onBeforeFetchPhysician(evt: FetchEvent): void {
    if (evt) {
      const target: FetchAPI = cast(evt.target);

      //@ts-ignore
      const state: ComponentState = cast(target.state);

      const dontHasInsurance = Boolean(this.context.get('isPrivateHealthInsurance'))
      const cdConvenio = dontHasInsurance ? '' : this.getInsuranceCardSelectedData()?.healthInsuranceId;

      const specialityData  = this.specialityFields[state.identifier].getValue()
        ? this.specialityFields[state.identifier].getValue() as unknown as SpecialityData
        : '';

      const { nrSequencia } = !this.wizard.getHiddenSteps().includes('location') && this.locationAutocomplete.getValue()
        ? this.locationAutocomplete.getValue() as unknown as LocationData
        : { nrSequencia: '0' };

      const params = {
        cdConvenio,
        ieParticular: dontHasInsurance,
        nrSeqLocal: nrSequencia,
      }

      if (specialityData && specialityData.cdEspecialidade) {
        Object.assign(params, {cdEspecialidade: specialityData.cdEspecialidade});
      }

      this.physicianFetches[state.identifier].setParameters(params);
    }
  }

  /**
   * Clears the days selected in the datepicker, and returns navigation to the current date. \
   * Warning: It does not clear the value, only the tempValue.
   */
  clearTempValueDatePicker(): void {
    this.datePickerAppointmentRange.setInitialDate(moment().utc().toISOString());
    this.datePickerAppointmentRange.setValue([]);
  }

  @OnAfterNavigate({ target: 'wizard-appointments' })
  async onAfterNavigateWizard(event: AbortableNavigateEvent<string>): Promise<void> {
    this.onWizardNavigate(event);
    let fieldSchedulingListSize = 1;
    const isOnlyConsultation = this.context.get('isOnlyConsultation') as boolean;
    if(event.from === 'consultation' && isOnlyConsultation) {
      fieldSchedulingListSize = Number(this.context.get('fieldSchedulingListSize'));
      if(fieldSchedulingListSize === 1) {
        const fieldSpeciality = this.fieldSpeciality.getValue() as FieldSpecialityProps;
        const fieldPhysician = this.fieldPhysician.getValue() as FieldPhysicianProps;
        const fieldMedicalArea = this.context.get('fieldMedicalAreaIsVisible') ? this.fieldMedicalArea.getValue() as MedicalArea : undefined;

        const cdEspecialidade = fieldSpeciality?.cdEspecialidade ? fieldSpeciality.cdEspecialidade : '';
        const cdPessoaFisica = fieldPhysician?.cdPessoaFisica ? fieldPhysician.cdPessoaFisica : '';
        const nrSeqMedicalArea = fieldMedicalArea?.nrSequencia;

        this.context.set({cdEspecialidade: cdEspecialidade, cdMedico: cdPessoaFisica, nrSeqMedicalArea: nrSeqMedicalArea});
      }
    }

    if(event.to === 'range-date') {
      // This forces the user to always click on DatePicker's select button.
      this.wizard.setStepDone('range-date', false);

      this.panelNotificationStepDateAPI.setVisible(false);
      this.setWaitingListDataToContext();
    }

    if(event.to === 'range-date' && event.from !== 'suggestion') {
      this.clearTempValueDatePicker();
    }

    if(event.from === 'suggestion') {
      if (this.panelDeck.isVisible()) {
        this.deckChoosenItems.setData([]);
      }
      if (this.panelBlockedItemsAPI.isVisible()) {
        this.deckBlockedItemsAPI.setData([]);
      }
    }

    if(event.to === 'range-date' && !isOnlyConsultation || event.to === 'range-date' && fieldSchedulingListSize > 1) {
      this.restoreInitialRangeDatePicker();
    } else if(event.to === 'range-date' && event.from !== 'suggestion' && isOnlyConsultation && fieldSchedulingListSize == 1) {

      const cdEspecialidade = String(this.context.get('cdEspecialidade')).trim();
      const cdMedico = String(this.context.get('cdMedico')).trim();

      if(cdEspecialidade.length === 0 || cdMedico.length === 0) {
        this.restoreInitialRangeDatePicker();
        return;
      }

      const response = await this.getAvailableCalendarDays();
      this.handleAvailableCalendarDaysResponse(response);
    }

    const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;
    const additionalInformationAlreadyExecuted = this.context.get('additionalInformationAlreadyExecuted') as boolean;
    if(event.to === 'exam-additional-information' && currentPatientSelected != null && !additionalInformationAlreadyExecuted) {
      this.context.set({ additionalInformationAlreadyExecuted: true })

      if(currentPatientSelected.id !== 0) {
        this.fieldCheckboxSaveAPI.setVisible(true);
        this.formAdditionalInformationAPI.setLoading(true);
        this.fetchAdditionalInformationAPI.setParameters({patientId: currentPatientSelected.id});
        this.fetchAdditionalInformationAPI.forceFetch();
      } else {
        this.formAdditionalInformationAPI.setLoading(false);
      }
    }

    if(event.from === 'exam-additional-information') {
      this.saveAdditionalInformation();
    }
  }

  private saveAdditionalInformation(): void {
    const campos: FieldTypes[] = [];
    const newValues = this.formAdditionalInformationAPI.getRecord() as unknown as AdditionalInformation;
    const oldValues = this.formAdditionalInformationAPI.getData() as unknown as AdditionalInformation;
    const body = { campos };

    Object.keys(newValues).forEach(propertyName => {
      const currentPropertyName = propertyName as keyof AdditionalInformation;
      if(propertyName !== 'saveAdditionalInfo' && oldValues && oldValues[currentPropertyName] !== newValues[currentPropertyName]) {
        const oldV = oldValues[currentPropertyName];
        const newV = newValues[currentPropertyName];
        body.campos.push({
          dsValorNew: newV ? newV : '',
          dsValorOld: oldV ? oldV : '',
          nmAtributo: currentPropertyName,
        });
      }
    });

    if(body.campos.length === 0 ){
      return ;
    }

    const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;
    if(newValues['saveAdditionalInfo'] && currentPatientSelected && currentPatientSelected.id !== 0) {
      this.sendUpdateRequest(body, currentPatientSelected.id).then(() => {
        this.formAdditionalInformationAPI.setData(this.formAdditionalInformationAPI.getRecord());
      });
    }
  }

  private sendUpdateRequest(body: Record<string, Value>, patientId: number): Promise<void> {
    return PatientHttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_INFORMATION, { patientId }, { body })
      .then((response) => {
        this.handleResponse(response);
      }).catch((error: HttpServiceError) => {
        this.handleResponse(cast(error.response));
      });
  }

  private handleResponse(resp: HttpResponse<unknown>): void {
    showToastNotificationMessage(resp.body as ToastResponse, undefined, 'patient.profile.health.information');
  }

  private handleAvailableCalendarDaysResponse(response: AvailableCalendarDaysProps): void {
    if(response) {
      const { availableDays, existsAnyBlockedItems } = response;
      if(availableDays) {
        availableDays.forEach(date =>  this.datePickerAppointmentRange.addRange(date, date));
      }
      const ieListaEspConsultaEnabled = 'S' === JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA);
      const isNotificationVisible = ieListaEspConsultaEnabled && availableDays == null && !existsAnyBlockedItems;
      this.panelNotificationStepDateAPI.setVisible(isNotificationVisible);
    } else {
      this.restoreInitialRangeDatePicker();
    }
  }

  @OnNavigate({target: 'date-picker-appointment-range'})
  async onNavigateDatePicker(event : AbortableNavigateEvent<string>): Promise<void> {
    this.panelNotificationStepDateAPI.setVisible(false);
    const fieldSchedulingListSize = Number(this.context.get('fieldSchedulingListSize'));
    const isOnlyConsultation = this.context.get('isOnlyConsultation');
    const cdEspecialidade = String(this.context.get('cdEspecialidade')).trim();
    const cdMedico = String(this.context.get('cdMedico')).trim();

    if(!isOnlyConsultation
      || !cdEspecialidade
      || !cdMedico
      || fieldSchedulingListSize > 1) {
      return;
    }

    const currentDate = moment.utc();
    const goToDate = moment.utc(event.to && event.to[0]);
    const lastCalendarDate = goToDate.add(1, 'months');

    if(lastCalendarDate > currentDate) {
      const ranges = this.datePickerAppointmentRange.getRanges();
      for(const range of ranges) {
        if(range.startDate.includes(lastCalendarDate.format(ISO_YEAR_MONTH_FORMAT))) {
          return;
        }
      }

      const response = await this.getAvailableCalendarDays(lastCalendarDate.format(ISO_DATE_FORMAT));
      this.handleAvailableCalendarDaysResponse(response);
    }
  }

  /**
   * - For empty lastCalendarDate, the available calendar days follow
   * the backend criteria.
   * - For not empty lastCalendarDate the available calendar
   * days are limited to date informed in the string.
   * @param lastCalendarDate of type string.
   * @returns string[] with available dates.
   */
  async getAvailableCalendarDays(lastCalendarDate = '' as string): Promise<AvailableCalendarDaysProps> {
    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.setInsurenceStepFields();

    const patientId = this.newScheduleFields.patient.patientId ? this.newScheduleFields.patient.patientId : UserContext.patientId;

    const paramsSeqAgenda: ParamsSeqAgenda = {
      cdCategoria: this.newScheduleFields.insurance?.category?.cdCategoria ?? '',
      cdConvenio: (String(this.newScheduleFields.insurance.convenio.cdConvenio)) || '',
      cdPlano: this.newScheduleFields.insurance?.plan?.cdPlano ?? '',
      healthCardNumber: this.newScheduleFields.insurance.healthCardNumber || '',
      healthCardValidity: this.newScheduleFields.insurance.healthCardValidity || '',
      locationId: '',
      nrSeqAgeint: 0,
      patientId: this.newScheduleFields.patient.patientId || '0',
    };

    this.setAdditionalInformationOnParams(paramsSeqAgenda);

    try {
      const waitingListData = this.context.get('waitingListData') as Record<string, Value>;
      this.datePickerAppointmentRange.clearRanges();
      this.datePickerAppointmentRange.setLoading(true);

      const { grupoEstab: locationData } = (this.isStepShowing('location') ? this.step2FormAPI.getRecord() : {}) as FormStep02Props;
      if(this.isReschedule) {
        const response = await HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
          lastCalendarDate,
          locationId: locationData?.nrSequencia || 0,
          nrSeqAgeint: RescheduleContext.nrSeqAgeint,
          patientId: patientId
        });
        this.datePickerAppointmentRange.setLoading(false);
        const result = response.body as AvailableCalendarDaysProps;
        this.context.set({waitingListData: {...waitingListData, nrSeqAgeint: RescheduleContext.nrSeqAgeint, nrSeqAgeintItem: result.nrSeqAgeintItem}});
        return result;
      }

      let response = await HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda});
      let nrSeqAgeint = response.body as number;
      const hasSeqAgendaIntegrada = nrSeqAgeint > 0;
      paramsSeqAgenda.nrSeqAgeint = nrSeqAgeint;

      const nrSeqAgeintObtained = await HttpService.get<IntegratedAgendaSequenceResponse>(
        APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA,
        {...paramsSeqAgenda}
      ).then(({ body }) => body?.value);

      nrSeqAgeint = nrSeqAgeint > 0 ? nrSeqAgeint : defaultIfUndefined(nrSeqAgeintObtained, 0);

      const paramsList = this.getParamsListAppointmentScheduleInfo(nrSeqAgeint, hasSeqAgendaIntegrada);

      response = await HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint, patientId }, {body: paramsList as unknown as Record<string, Value>});
      if(response.status === 204) {
        response = await HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
          lastCalendarDate,
          locationId: locationData?.nrSequencia || 0,
          nrSeqAgeint: nrSeqAgeint,
          patientId: patientId
        });
        this.datePickerAppointmentRange.setLoading(false);
        const result = response.body as AvailableCalendarDaysProps;

        this.context.set({waitingListData: {...waitingListData, nrSeqAgeint: nrSeqAgeint, nrSeqAgeintItem: result.nrSeqAgeintItem}});
        return result;
      }

      return {
        availableDays: [],
        existsAnyBlockedItems: false,
        nrSeqAgeint,
        nrSeqAgeintItem: '',
      } as AvailableCalendarDaysProps;
    } catch(error) {
      this.restoreInitialRangeDatePicker();
      this.datePickerAppointmentRange.setLoading(false);
      return {
        availableDays: [],
        nrSeqAgeintItem: '',
      } as unknown as AvailableCalendarDaysProps;
    }
  }

  setAvailableCalendarDaysForReschedule(data: PhysicianAndSpeciality, nrSeqAgeint: number): void {
    const { cdEspecialidade, cdMedico } = data;

    this.context.set({
      cdEspecialidade,
      cdMedico,
      fieldSchedulingListSize: 1,
      isOnlyConsultation: true,
    });
    this.clearTempValueDatePicker();

    try {
      this.datePickerAppointmentRange.clearRanges();
      this.datePickerAppointmentRange.setLoading(true);

      HttpService.get(APPLICATION_ENDPOINTS.AVAILABLE_CALENDAR_DAY, {
        lastCalendarDate: '',
        nrSeqAgeint,
        patientId: UserContext.patientId
      })
        .then(calendarResponse => {
          this.datePickerAppointmentRange.setLoading(false);
          const availableDays = calendarResponse.body as [];
          availableDays.forEach(date =>  this.datePickerAppointmentRange.addRange(date, date));
        });
    } catch(error) {
      this.restoreInitialRangeDatePicker();
      this.datePickerAppointmentRange.setLoading(false);
    }
  }

  @OnChange({ target: 'field-scheduling-list' })
  onChangeFieldMedicalArea(event: ChangeEvent<ConsultFieldItem>): void {
    if (event) {
      let isFinished = true;
      if(Array.isArray(event.value)) {
        const listItems = event.value as ConsultFieldItem[];
        this.context.set({fieldSchedulingListSize: listItems.length})

        isFinished = listItems.every(item => {
          const specialityCode = item.specialityData?.cdEspecialidade;
          const fieldPhysician = item.physician;

          return specialityCode != null || fieldPhysician?.cdPessoaFisica != null;
        })
      }
      setTimeout(() => {
        this.wizard.setStepDone('consultation', isFinished);
      })
    }
  }

  async isValidFieldsForConsultation(): Promise<boolean> {
    if(this.isReschedule) {
      return true;
    }

    const fieldSpeciality = this.fieldSpeciality.getValue() as FieldSpecialityProps;
    const fieldPhysician = this.fieldPhysician.getValue() as FieldPhysicianProps;
    return (await this.fieldPhysician.validate() || await this.fieldSpeciality.validate())
      && (fieldPhysician?.cdPessoaFisica != null || fieldSpeciality?.cdEspecialidade != null);
  }

  onChangeSeg(event: ChangeEvent<DefaultAllowedScheduling[]>): void {
    const scheduleTypes = event.value as unknown as SegmentedControlProps[];

    const isOnlyConsultation = scheduleTypes.length === 1 && scheduleTypes[0].value === 'schedule-type.consultation';
    this.context.set({
      isOnlyConsultation,
      scheduleLength: scheduleTypes.length
    });
  }

  @OnChange({ target: 'field-exams-to-schedule' })
  onChangeScheduleExamListField(event: ChangeEvent<ExamSchedule>): void {
    if (event) {
      let isFinished = true;
      if (Array.isArray(event.value)) {
        const listItems: ExamSchedule[] = event.value
        isFinished = listItems.every(item => {
          return Object.keys(removeEmptyProperties({...item})).length;
        });
      }

      setTimeout(() => {
        this.wizard.setStepDone('exams', isFinished);
      });
    }
  }

  onWizardNavigate(event: AbortableNavigateEvent<string>): void {
    switch(event.to) {
      case WizardAppointmentsIdentifies.CONSULTATION:
        this.onNavigateToConsultation();
        break;
      case WizardAppointmentsIdentifies.EXAMS:
        this.onNavigateToExams();
        break;
      case WizardAppointmentsIdentifies.EXAM_ADDITIONAL_INFORMATION:
        this.onNavigateToExamAdditionalInformation();
        break;
      case 'scheduling-for':
        this.checkStepTwo();
        break;
      case WizardAppointmentsIdentifies.QUIZ:
        this.controlQuizWizardStep(event.from);
        break;
      case WizardAppointmentsIdentifies.LOCATION:
        this.onNavigateToLocation(event.from);
        break;
      case 'suggestion':
        this.sendInfo();
        break;
    }
  }

  private controlQuizWizardStep = (from: string): void => {
    this.setValuesNewScheduleFields();

    this.quizFormAPI.setEditing(true);
    this.quizFormAPI.setLoading(true);
    if((!this.newScheduleFieldsOldValues || !this.newScheduleFields) || !this.checkIfNewScheduleFieldsWasChanged(this.newScheduleFieldsOldValues, this.newScheduleFields, true)){
      this.quizDynamicFieldsAPI.restoreState(true);
      this.sendAppointmesItems().then(() => this.setQuizMetadata());
    } else if((from === WizardAppointmentsIdentifies.CONSULTATION || from === WizardAppointmentsIdentifies.EXAM_ADDITIONAL_INFORMATION) && (!this.dynamicMetada || !this.dynamicMetada.children)) {
      this.wizard.setStepDone(WizardAppointmentsIdentifies.QUIZ, this.isStepQuizDone);
      this.wizard.nextStep();
    } else if((from === WizardAppointmentsIdentifies.RANGE_DATE ) && (!this.dynamicMetada || !this.dynamicMetada.children)) {
      this.wizard.setStepDone(WizardAppointmentsIdentifies.QUIZ, this.isStepQuizDone);
      this.wizard.prevStep();
    }else {
      this.isStepQuizDone = true;
      this.checkRequiredFieldsStepQuiz(this.dynamicMetada.children);
      this.wizard.setStepDone(WizardAppointmentsIdentifies.QUIZ, this.isStepQuizDone);
      this.quizFormAPI.setLoading(false);
    }
  }

  private checkIfNewScheduleFieldsWasChanged = (oldScheduleFields: Record<string, Value>, newScheduleFields: Record<string, Value>, fieldsWasChanged: boolean): boolean => {
    const oldValuesKeys = Object.keys(oldScheduleFields);
    const newValuesKeys = Object.keys(newScheduleFields);

    if (oldValuesKeys.length !== newValuesKeys.length) {
      fieldsWasChanged = false;
      return fieldsWasChanged;
    }

    oldValuesKeys.forEach(value => {
      const oldValue = oldScheduleFields[value] as Record<string, Value>;
      const newValue = newScheduleFields[value] as Record<string, Value>;
      const areObjects = ObjectComparatorUtils.isObject(oldValue) && ObjectComparatorUtils.isObject(newValue);

      if (areObjects && !this.checkIfNewScheduleFieldsWasChanged(oldValue, newValue, fieldsWasChanged) || !areObjects && oldValue !== newValue) {
        fieldsWasChanged = false;
        return;
      }

    });

    return fieldsWasChanged;
  }

  private setValuesNewScheduleFields = (): void => {
    this.newScheduleFieldsOldValues = this.newScheduleFields;

    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.setInsurenceStepFields();
    this.setScheduleTypeFields();

    if (this.haveLocation) {
      this.setLocationFields();
    }

    this.setDateRange();
  }

  private sendAppointmesItems = async (): Promise<HttpResponse<unknown> | void> => {
    const paramsList: AppointmentScheduleInfo[] = await this.getAppointmentScheduleInfoParams();

    return await HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint: this.nrSeqAgeint, patientId: this.newScheduleFields.patient.patientId }, {
      body: paramsList as unknown as Record<string, Value>
    }).catch(() => this.callToastLoadDataError());
  }

  private getAppointmentScheduleInfoParams = async (): Promise<AppointmentScheduleInfo[]> => {
    const paramsSeqAgenda: ParamsSeqAgenda = {
      cdCategoria: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.category && this.newScheduleFields.insurance.category.cdCategoria) || '',
      cdConvenio: (String(this.newScheduleFields.insurance.convenio.cdConvenio)) || '',
      cdPlano: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.plan && this.newScheduleFields.insurance.plan.cdPlano) || '',
      healthCardNumber: this.newScheduleFields.insurance.healthCardNumber || '',
      healthCardValidity: this.newScheduleFields.insurance.healthCardValidity || '',
      locationId: '',
      nrSeqAgeint: 0,
      patientId: this.newScheduleFields.patient.patientId || '0',
    };

    this.setAdditionalInformationOnParams(paramsSeqAgenda);

    if(this.nrSeqAgeint != null && this.nrSeqAgeint > 0){
      return this.getParamsListAppointmentScheduleInfo(this.nrSeqAgeint, true);
    }

    this.nrSeqAgeint = 0;
    const nrSeqAgeintPrevSeq = await HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda})
      .then((response) => response.body as number)
      .catch(() => this.callToastLoadDataError())

    const hasSeqAgendaIntegrada = nrSeqAgeintPrevSeq != null && nrSeqAgeintPrevSeq > 0;
    paramsSeqAgenda.nrSeqAgeint = hasSeqAgendaIntegrada ? nrSeqAgeintPrevSeq : 0;

    const integratedAgendaSequence = await HttpService.get<IntegratedAgendaSequenceResponse>(APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda})
      .then(response => response.body)
      .catch(() => this.callToastLoadDataError());

    const nrSeqAgeint = hasSeqAgendaIntegrada ? nrSeqAgeintPrevSeq : integratedAgendaSequence?.value;
    this.nrSeqAgeint = nrSeqAgeint != null ? nrSeqAgeint : 0;

    return this.getParamsListAppointmentScheduleInfo(this.nrSeqAgeint, hasSeqAgendaIntegrada);
  }

  private getParamsListAppointmentScheduleInfo = (nrSeqAgeint: number, hasSeqAgendaIntegrada: boolean): AppointmentScheduleInfo[] => {
    const paramsList: AppointmentScheduleInfo[] = [];
    if (this.newScheduleFields.consultation && this.newScheduleFields.consultation.length > 0) {
      this.newScheduleFields.consultation.forEach(consult => {
        paramsList.push({
          cdEspecialidade: (consult?.specialityData?.cdEspecialidade) || '',
          cdMedico: (consult.physician && consult.physician.cdPessoaFisica) || '',
          hasSeqAgendaIntegrada: hasSeqAgendaIntegrada,
          ieTipoItem: NewScheduleTypes.CONSULTATION,
          nrSeqAgeint: nrSeqAgeint,
          nrSeqMedicalArea: consult.medicalArea?.nrSequencia,
        });
      });
    }

    if (this.newScheduleFields.exam && this.newScheduleFields.exam.length > 0) {
      this.newScheduleFields.exam.forEach(exam => {
        paramsList.push({
          hasSeqAgendaIntegrada: hasSeqAgendaIntegrada,
          ieTipoItem: NewScheduleTypes.EXAM,
          nrSeqAgeint: nrSeqAgeint,
          nrSeqProcInterno: exam.nrSeqProcInterno,
        });
      });
    }

    return paramsList;
  }

  private setQuizMetadata = (): void => {
    const integratedAgendaSequence = this.nrSeqAgeint;

    const params = {
      readOnly: DefaultQuizParams.READ_ONLY,
      seqAgeInt: integratedAgendaSequence,
      transfer: DefaultQuizParams.TRANSFER,
    }

    HttpService.get<CustomMetadata>(APPLICATION_ENDPOINTS.GET_QUIZ_METADATA, params)
      .then(response => {
        const { body } = response;

        this.dynamicMetada = body as CustomMetadata;
        const metaData = body as Metadata;

        this.quizDynamicFieldsAPI.setMetadata(metaData);
        if(!this.dynamicMetada || !this.dynamicMetada.children){
          this.wizard.setStepDone(WizardAppointmentsIdentifies.QUIZ, true);
          this.wizard.nextStep();
          return;
        }

        this.isStepQuizDone = true;
        this.checkRequiredFieldsStepQuiz(this.dynamicMetada.children);
        this.wizard.setStepDone(WizardAppointmentsIdentifies.QUIZ, this.isStepQuizDone);

        this.quizFormAPI.setRecord({});
        this.quizFormAPI.setLoading(false);
      });
  }

  @OnDynamicContentRender({ target: 'deck-choosen-items' })
  onDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const scheduleItem = event.data as ScheduleItem[];
      const hasSchedulingTimes = scheduleItem.some(item => item.hasSchedulingTimes);
      const renderSuggestionDate: () => JSX.Element = () => {
        return (
          <>
            {hasSchedulingTimes && <div className="wrapper-full-click" onClick={() => this.openModalSuggestion(scheduleItem[0].groupId)}></div>}
            <div className='deck-title-suggestion-group'>
              {hasSchedulingTimes
                ?
                <Button
                  value={MessageService.translate('patient.choose-combo')}
                  theme={ButtonTheme.QUIET_EMPHASIS}
                  icon='/static/assets/icons/DLS_Calendar_32.svg'
                  iconPlacement={Placement.RIGHT}
                  onClick={() => this.openModalSuggestion(scheduleItem[0].groupId)}
                />
                : <p className='p-deck-title-label__not-has-scheduling'>{MessageService.translate('suggestion.lbl-not-has-scheduling')}</p>
              }
            </div>
          </>
        );
      }
      render(renderSuggestionDate);
    }
  }

  @OnDynamicContentRender({ target: 'deck-blocked-items' })
  handleDeckBlockedItemsRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const renderBlockedSuggestions: () => JSX.Element = () => {
        return (
          <div className='deck-title-suggestion-group'>
            <p className='p-deck-title-label__not-has-scheduling'>{ this.state.blockedCustomMessage ? this.state.blockedCustomMessage : MessageService.translate('tws.label-blocked-by-health-insurance')}</p>
          </div>
        );
      }
      render(renderBlockedSuggestions);
    }
  }

  @OnSubmit({ target: 'wizard-appointments' })
  onFinishWizard(event: SubmitEvent): void {
    const waitingListData = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    if (event) {
      setTimeout(() => {
        this.panelLoader.setVisible(true);
        this.loaderFinalAPI.setMessage(MessageService.translate('tws.loader.loading'));
      });
      this.wizard.setStepDone('suggestion', false);
      let confirmAppointmentInfoList: Record<string, Value>[] = [];
      if(this.suggestionList && this.suggestionList.length > 0) {
        this.suggestionList.forEach(suggestion => {
          const waitingListItem = waitingListData.find(item => item.nrSeqAgeintItem === suggestion.nrSeqAgeintItem);
          if(!waitingListItem && suggestion.hasSchedulingTimes) {
            confirmAppointmentInfoList.push({
              attachedFileList: suggestion.attachedPrescheduleFiles,
              availableType: suggestion.availableType,
              cdAgenda: suggestion.agendaId,
              cdEspecialidade: suggestion.cdEspecialidade,
              cdEstabelecimento: suggestion.cdEstabelecimento,
              cdMedico: suggestion.cdMedico && suggestion.cdMedico.length > 0 ? suggestion.cdMedico : null,
              cdPessoaFisica: this.newScheduleFields.patient.patientId,
              dsNota: suggestion.observation,
              dtAgenda: suggestion.selectedDate,
              ieTipoAgendamento: suggestion.type,
              nmPreferredTime: suggestion.period,
              nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
              nrSeqAgenda: suggestion.nrSeqAgenda,
              nrSeqProcInterno: suggestion.nrSeqProcInterno,
              quantityClassification: suggestion.quantityClassification,
              quantityClassificationTelehealth: suggestion.quantityClassificationTelehealth,
              ruleClassification: suggestion.ruleClassification,
              ruleClassificationTelehealth: suggestion.ruleClassificationTelehealth,
              suggestionTypeConsultation: suggestion.suggestionTypeConsultation ?? suggestion.teleconsultationType,
            });
          }
        });
      }
      if(waitingListData.length > 0) {
        confirmAppointmentInfoList = [...confirmAppointmentInfoList, ...waitingListData as unknown as Record<string, Value>[]];
      }

      const patientCode = this.newScheduleFields.patient.patientId ? Number.parseInt(this.newScheduleFields.patient.patientId) : UserContext.patientId;
      AppointmentService.confirmAndRequestAppointments(this.nrSeqAgeint, patientCode, confirmAppointmentInfoList).then(body => {
        this.wizard.setStepDone('suggestion', false);
        ModalService.openFullscreen({
          className: 'schedule-confirmation-modal-custom-bg',
          closeable: true,
          contextData: body,
          dockButtons: [],
          fragment: 'schedule-confirmation',
          onClose: () => {
            this.panelLoader.setVisible(false);
            this.historyService.push(APPLICATION_ROUTES.APPOINTMENTS, {}, {scrollToTop: true});
          },
          title: MessageService.translate('patient.schedule-success'),
        })
      }).catch(() => {
        this.wizard.setStepDone('suggestion', true);
        showToastNotificationError(MessageService.translate('patient.appointments.confirm-failed'));
      }).finally(() => {
        this.panelLoader.setVisible(false);
      })
    }
  }

  getSuggestions(nrSeqAgeint: number, patientId: number): void {
    const { grupoEstab: locationData } = (this.isStepShowing('location') ? this.step2FormAPI.getRecord() : {}) as FormStep02Props;
    HttpService.get(APPLICATION_ENDPOINTS.SUGGESTION_DATES, {
      finalDate: this.newScheduleFields.dataRage.end,
      initialDate: this.newScheduleFields.dataRage.start,
      locationId: locationData?.nrSequencia || 0,
      nrSeqAgeint: nrSeqAgeint,
      patientId: patientId,
    }).then(response => {
      const ieListaEspConsulta = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA);
      const ieListaEspExame = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_EXAME);
      const isEnabledToScheduleWaitingList = ieListaEspConsulta === 'S' && ieListaEspExame === 'S';
      const suggestionGroups = response.body as SuggestionGroups[];
      const isShowPrice = this.isShowSchedulePrice();

      let groupId = 0
      let isShowNotificationWaitingList = false;
      this.suggestionList = [] as ScheduleItem[];
      const blockedSuggestions = [] as ScheduleItem[];
      suggestionGroups.forEach(group => {
        if(!isShowNotificationWaitingList) {
          isShowNotificationWaitingList = (
            (!group.establishmenSuggestionsGroups || group.establishmenSuggestionsGroups.length === 0) &&
            (!group.blockedItems || group.blockedItems.length === 0)
          )
        }
        group.groupId = groupId;
        group.nrSeqAgeint = nrSeqAgeint;
        group.isShowPrice = isShowPrice;
        group.scheduleItems && group.scheduleItems.forEach(suggestion => {
          this.suggestionList.push({
            agendaId: '',
            availableType: suggestion.availableType,
            cdEspecialidade: suggestion.cdEspecialidade || '',
            cdEstabelecimento: suggestion.cdEstabelecimento,
            cdMedico: suggestion.cdMedico || '',
            date: suggestion.date,
            dsEstablishment: '',
            dsProcInterno: suggestion.dsProcInterno,
            endDate: '',
            groupId: group.groupId,
            hasSchedulingTimes: group.establishmenSuggestionsGroups.length > 0,
            ieAcao: '',
            ieReturn: suggestion.ieReturn,
            image: suggestion.image,
            isEnabledToScheduleWaitingList,
            isItemFromReschedule: this.isReschedule,
            isShowPrice: suggestion.isShowPrice,
            key: suggestion.key,
            nrMinutoDuracao: 0,
            nrSeqAgeint: suggestion.nrSeqAgeint,
            nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
            nrSeqAgenda: '',
            nrSeqProcInterno: suggestion.nrSeqProcInterno,
            observation:suggestion.observation,
            period: suggestion.period,
            price: '0',
            priceNoFormatted: 0,
            qtPesoMaxAgenda: '',
            quantityClassification : suggestion.quantityClassification,
            quantityClassificationTelehealth : suggestion.quantityClassificationTelehealth,
            ruleClassification : suggestion.ruleClassification,
            ruleClassificationTelehealth : suggestion.ruleClassificationTelehealth,
            selectedDate: '',
            shouldDisplayTheConsultationValue:'N',
            shouldDisplayTheExamValue: 'N',
            startDate: '',
            subtitle1: suggestion.subtitle1,
            suggestionGroupId: groupId,
            suggestionId: suggestion.suggestionId,
            suggestionTypeConsultation: suggestion.suggestionTypeConsultation,
            title: suggestion.title,
            type: suggestion.type,
          });
        });
        groupId++;
        group.blockedItems && group.blockedItems.forEach(blockedItem => {
          blockedSuggestions.push({
            dsEstablishment: '',
            groupId: -1,
            isShowPrice: false,
            selectedDate: '',
            title: blockedItem.dsItemGridAgeint,
          } as ScheduleItem);
        });
      })
      setTimeout(() => {
        const doesNotExistsSuggestions = this.suggestionList.every(suggestion => !suggestion.hasSchedulingTimes);
        this.context.set({ allSuggestionsHaveBeenSelected: false, doesNotExistsSuggestions  });
        this.panelLoader.setVisible(false);
        this.panelNotificationStepSuggestionAPI.setVisible(isShowNotificationWaitingList && isEnabledToScheduleWaitingList && !this.isReschedule);
        if(this.suggestionList.length > 0) {
          this.panelDeck.setVisible(true);
          this.suggestionsAfterGetData = this.suggestionList;
          this.context.set({ suggestionGroups: suggestionGroups });
        }
        if(blockedSuggestions.length > 0) {
          HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { action: 'BA' }).then(resp => {
            const customMessage = resp.body as PseudonymCustomMenssage;
            this.state.blockedCustomMessage = !customMessage.message ? '' : customMessage.message
          }).finally(() => {
            this.panelBlockedItemsAPI.setVisible(true);
            this.blockedSuggestionsAfterGetData = blockedSuggestions;
          });
        }
      })
    }).catch(this.callToastLoadDataError);
  }

  sendInfo(): void {
    this.context.set({ waitingListArrayToPersistence: [] });
    this.panelNotificationStepSuggestionAPI.setVisible(false);

    if (this.panelTotalAPI.isVisible()) {
      this.totalText.setValue('');
    }

    this.panelLoader.setVisible(true);

    if (this.panelDeck.isVisible()) {
      this.deckChoosenItems.setData([]);
      this.deckChoosenItems.setDone(true);
    }

    if (this.panelBlockedItemsAPI.isVisible()) {
      this.deckBlockedItemsAPI.setData([]);
      this.deckBlockedItemsAPI.setDone(true);
    }

    this.panelDeck.setVisible(false);
    this.panelBlockedItemsAPI.setVisible(false);
    this.context.set({ suggestionGroups: [] });

    if(this.isReschedule) {
      this.sendRescheduleInfo();
    } else {
      this.sendScheduleInfo();
    }
  }

  private setAdditionalInformationOnParams(paramsSeqAgenda: ParamsSeqAgenda): void {
    if(this.isStepShowing('exam-additional-information')) {
      const additionalInformation = this.formAdditionalInformationAPI.getRecord() as unknown as AdditionalInformation;
      paramsSeqAgenda.patientHeight = additionalInformation.heightInCentimeters;
      paramsSeqAgenda.patientWeight = additionalInformation.weight;
    }
  }

  sendScheduleInfo(): void {
    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.setInsurenceStepFields();
    this.setScheduleTypeFields();
    if (this.haveLocation) {
      this.setLocationFields();
    }
    this.setDateRange();

    const paramsSeqAgenda: ParamsSeqAgenda = {
      cdCategoria: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.category && this.newScheduleFields.insurance.category.cdCategoria) || '',
      cdConvenio: (String(this.newScheduleFields.insurance.convenio.cdConvenio)) || '',
      cdPlano: (this.newScheduleFields.insurance && this.newScheduleFields.insurance.plan && this.newScheduleFields.insurance.plan.cdPlano) || '',
      healthCardNumber: this.newScheduleFields.insurance.healthCardNumber || '',
      healthCardValidity: this.newScheduleFields.insurance.healthCardValidity || '',
      locationId: '',
      nrSeqAgeint: 0,
      patientId: this.newScheduleFields.patient.patientId || '0',
    };

    this.setAdditionalInformationOnParams(paramsSeqAgenda);

    if (this.isQuizAvailable && this.nrSeqAgeint != null && this.nrSeqAgeint > 0) {
      this.getSuggestions(this.nrSeqAgeint, Number(paramsSeqAgenda.patientId || 0));
      return;
    }

    this.nrSeqAgeint = 0;

    const { panelLoader } = this;
    HttpService.get(APPLICATION_ENDPOINTS.GET_PREVIOUS_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda}).then(responsePrevSeq => {
      let nrSeqAgeint = responsePrevSeq.body as number;
      const hasSeqAgendaIntegrada = nrSeqAgeint > 0;
      paramsSeqAgenda.nrSeqAgeint = nrSeqAgeint;

      HttpService.get<IntegratedAgendaSequenceResponse>(APPLICATION_ENDPOINTS.GET_SEQ_AGENDA_INTEGRADA, {...paramsSeqAgenda}).then(async responseSegSchedule => {
        nrSeqAgeint = nrSeqAgeint > 0 ? nrSeqAgeint : responseSegSchedule.body?.value as number;
        this.nrSeqAgeint = nrSeqAgeint;

        const paramsList: AppointmentScheduleInfo[] = this.getParamsListAppointmentScheduleInfo(this.nrSeqAgeint, hasSeqAgendaIntegrada);

        HttpService.post(APPLICATION_ENDPOINTS.GET_SEQ_PROC_INTERNO, { nrSeqAgeint, patientId: paramsSeqAgenda.patientId }, {
          body: paramsList as unknown as Record<string, Value> })
          .then(() => {
            this.getSuggestions(nrSeqAgeint, Number(paramsSeqAgenda.patientId || 0));
          }).catch(() => {
            this.callToastLoadDataError();
            panelLoader.setVisible(false);
          });
      }).catch(() => {
        this.callToastLoadDataError();
        panelLoader.setVisible(false);
      });
    }).catch(() => {
      this.callToastLoadDataError();
      panelLoader.setVisible(false);
    })
  }

  private callToastLoadDataError(): void {
    showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
  }

  sendRescheduleInfo(): void {
    const { patientId } = UserContext
    this.newScheduleFields = {} as unknown as NewScheduleFields;
    this.newScheduleFields.patient = {
      nmPessoaFisica: '',
      patientId: patientId?.toString(),
    };

    if (this.haveLocation) {
      this.setLocationFields();
    }

    this.setDateRange();
    this.nrSeqAgeint = RescheduleContext.nrSeqAgeint;
    this.getSuggestions(RescheduleContext.nrSeqAgeint, patientId);
  }

  setDateRange(): void {
    this.newScheduleFields.dataRage = this.selectedDateRange;
  }

  openModalSuggestion(groupId: number): void {
    AppointmentContext.callUpdateAppointmentCard = this.setSelectedSuggestion.bind(this);
    const suggestionGroups = this.context.get('suggestionGroups') as SuggestionGroups[];
    ModalService.openFullscreen({
      closeable: true,
      contextData: {
        allowSelectDesiredDate: this.context.get('allowSelectDesiredDate'),
        data: suggestionGroups[groupId] as unknown as Record<string, string>,
        desiredDateCustomMessage: this.context.get('desiredDateCustomMessage'),
        nrSeqAgeint: RescheduleContext.nrSeqAgeint
      },
      dockButtons: [],
      fragment: 'sugestion-list', //fix name
      onSubmit: () => undefined,
      title: '',
    });
  }

  private checkReservationTime(newSelectedList: ScheduleItem[]): ClearTimeReservation[] {
    const clearTimeReservationList: ClearTimeReservation[] = [];
    for(const newItem of newSelectedList) {
      const itemToRemove = this.suggestionList.filter(currentItemSet =>
        Number(currentItemSet.nrSeqAgeintItem) === Number(newItem.nrSeqAgeintItem) &&
        currentItemSet.availableType !== 'R' && newItem.availableType === 'R'
      );

      if(itemToRemove.length === 1) {
        clearTimeReservationList.push({nrSeqAgeintItem: Number(itemToRemove[0].nrSeqAgeintItem)});
      }
    }

    return clearTimeReservationList;
  }

  private async registerTimeSchedule(groupId: number, selectedList: ScheduleItem[]): Promise<boolean> {
    try {
      const clearTimeReservationList = this.checkReservationTime(selectedList);
      if(clearTimeReservationList.length > 0) {
        await this.clearTimeReservation(clearTimeReservationList);
      }

      let isReservationRegistered = true;
      const suggestionGroups = this.context.get('suggestionGroups') as SuggestionGroups[];
      for(const suggestion of selectedList) {
        if(suggestion.availableType !== 'R') {
          const bodyData = {
            cdAgenda: suggestion.agendaId,
            cdMedico: suggestion.cdMedico,
            hrAgenda: suggestion.startDate,
            ieAcao: 'I',
            nrMinutoDuracao: suggestion.nrMinutoDuracao,
            nrSeqAgeint: suggestionGroups[groupId].nrSeqAgeint,
            nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
            nrSeqAgenda: suggestion.nrSeqAgenda,
            nrSeqCombo: suggestion.suggestionId,
            qtPesoMaxAgenda: suggestion.qtPesoMaxAgenda === '0' ? '' : suggestion.qtPesoMaxAgenda,
          }
          if (suggestion.suggestionTypeConsultation == TeleconsultationType.TELECONSULTATION && suggestion.quantityClassificationTelehealth > 0) {
            bodyData.nrMinutoDuracao = suggestion.quantityClassificationTelehealth;
          } else if (suggestion.suggestionTypeConsultation == TeleconsultationType.IN_PERSON && suggestion.quantityClassification > 0) {
            bodyData.nrMinutoDuracao = suggestion.quantityClassification;
          }
          const response = await HttpService.post<TimeReservationResponse>(APPLICATION_ENDPOINTS.TIME_RESERVATION, { patientId: UserContext.patientId }, { body: bodyData });
          const message = response.body?.abortMessage;
          if(message != null && message.length > 0) {
            this.openModalSuggestion(groupId);
            showToastNotificationWarning(message);
            isReservationRegistered = false;
          }
        }
      }
      return isReservationRegistered;
    } catch(error) {
      this.callToastLoadDataError();
      return false;
    }
  }

  private async clearTimeReservation(clearTimeReservationList: ClearTimeReservation[]): Promise<HttpResponse<boolean>> {
    return HttpService.delete(APPLICATION_ENDPOINTS.CLEAR_TIME_RESERVATION, { patientId: UserContext.patientId },
      {body: clearTimeReservationList as unknown as Record<string, Value>}
    );
  }

  setSelectedSuggestion(groupId: number, selectedList: ScheduleItem[] = []): void {
    if (selectedList.length == 0) {
      return;
    }

    this.registerTimeSchedule(groupId, selectedList).then(isReservationRegistered => {
      if(!isReservationRegistered) {
        return;
      }

      let total = 0;
      let currencySymbol = '';
      let allSelected = true;
      const selectedDates = selectedList;
      const waitingListData = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
      this.suggestionList.forEach(item => {
        const selectedDate = selectedDates.find(suggestion => {
          return item.nrSeqAgeintItem.toString() === suggestion.nrSeqAgeintItem.toString();
        });
        if (item.groupId === groupId) {
          if (selectedDate) {
            item.agendaId = selectedDate.agendaId;
            item.availableType = selectedDate.availableType;
            item.cdEspecialidade = selectedDate.cdEspecialidade;
            item.cdEstabelecimento = selectedDate.cdEstabelecimento;
            item.cdMedico = selectedDate.cdMedico;
            item.dsEstablishment = selectedDate.dsEstablishment;
            item.ieAcao = selectedDate.ieAcao;
            item.ieReturn = selectedDate.ieReturn;
            item.image = selectedDate.image;
            item.nrSeqAgenda = String(selectedDate.nrSeqAgenda);
            item.nrSeqProcInterno = selectedDate.nrSeqProcInterno;
            item.observation = selectedDate.observation;
            item.period = selectedDate.period;
            item.price = selectedDate.price;
            item.priceNoFormatted = selectedDate.priceNoFormatted;
            item.qtPesoMaxAgenda = String(selectedDate.qtPesoMaxAgenda);
            item.selectedDate = selectedDate.date;
            item.startDate = selectedDate.startDate;
            item.subtitle1 = selectedDate.subtitle1;
            item.title = selectedDate.title;
            item.isShowPrice = selectedDate.isShowPrice;
            item.attachedPrescheduleFiles = selectedDate.attachedPrescheduleFiles;
            item.appointmentType = selectedDate.appointmentType;
            item.shouldDisplayTheConsultationValue = selectedDate.shouldDisplayTheConsultationValue;
            item.shouldDisplayTheExamValue= selectedDate.shouldDisplayTheExamValue;
            item.nrSeqAgeint = selectedDate.nrSeqAgeint,
            item.teleconsultationType = selectedDate.teleconsultationType
            item.suggestionTypeConsultation = selectedDate.suggestionTypeConsultation
            item.quantityClassification = selectedDate.quantityClassification;
            item.quantityClassificationTelehealth = selectedDate.quantityClassificationTelehealth;
            item.ruleClassification = selectedDate.ruleClassification;
            item.ruleClassificationTelehealth = selectedDate.ruleClassificationTelehealth;
          }
        } else if(!selectedDate){
          const isOnTheWaitingList = waitingListData.some(row => Number(row.nrSeqAgeintItem) === Number(item.nrSeqAgeintItem));
          item.isWaitingListItem = isOnTheWaitingList;
          item.removeItemFromWaitingList = this.removeWaitingListItemToContext.bind(this);
          item.callToWaitlistModal = this.showWaitingListModal.bind(this);
        }
        if (!item.ieReturn) {
          const matched = item.price.trim().match(/(\S+) /);
          if(matched != null && !currencySymbol) {
            currencySymbol = matched[0];
          }
          total = item.priceNoFormatted + total;
        }
        if (!item.selectedDate && item.hasSchedulingTimes) {
          allSelected = false;
        }
      })
      this.setTotalTotalScheduleAndShow(total, this.suggestionList, currencySymbol);
      this.deckChoosenItems.setData(this.suggestionList as unknown as Record<string, Value>[]);
      this.deckChoosenItems.setDone(true);
      this.context.set({ allSuggestionsHaveBeenSelected: allSelected });
      if (allSelected) {
        this.wizard.setStepDone('suggestion', true);
      } else {
        this.wizard.setStepDone('suggestion', false);
      }
    });
  }

  setTotalTotalScheduleAndShow(total: number, suggestionItems: ScheduleItem[],currencySymbol?: string): void {
    const isShowPrice = this.isShowSchedulePrice() && AppointmentUtils.isShowTotal(suggestionItems);
    this.panelTotalAPI.setVisible(isShowPrice);

    if(isShowPrice) {
      const totalFormatted = NumberFormatUtils.formatToCurrentLanguage(total, currencySymbol);
      const totalLabel = ' ' + ParseHelperUtils.parseToTextHtml(totalFormatted);
      this.totalText.setValue(MessageService.translate('tws.label-total') + totalLabel);
    }
  }

  isShowSchedulePrice(): boolean {
    if(this.isReschedule) {
      const contextValue = this.context.get('healthInsuranceIdToReschedule') || this.cdConvenioParticular;
      const healthInsuranceIdReschedule = contextValue != null ? contextValue : this.cdConvenioParticular;
      this.newScheduleFields.insurance = {
        autorizationCode: '',
        category: {
          cdCategoria: '',
          dsCategoria: '',
        },
        convenio: {
          cdConvenio: healthInsuranceIdReschedule.toString(),
          dsConvenio: '',
        },
        hasInsurance: Number(healthInsuranceIdReschedule) !== Number(this.cdConvenioParticular),
        plan: {
          cdPlano: '',
          dsPlano: ''
        },
      }
    }
    const healthInsuranceId = Number(this.newScheduleFields.insurance.convenio.cdConvenio);
    const privateHealthInsuranceId = Number(this.cdConvenioParticular);
    return healthInsuranceId === privateHealthInsuranceId;
  }

  showLocationStep(healthInsuranceId: number): void {
    HttpService.get(APPLICATION_ENDPOINTS.NEW_APPOINTMENT_ESTABLISHMENT_LOCALE, {cdConvenio: healthInsuranceId} ).then(response => {
      if (response && response.body) {
        const locationList = response.body as [];
        this.haveLocation = locationList.length > 0;
        if (!this.isReschedule && healthInsuranceId == 0) {
          this.controlLocationStepShow(locationList.length);
        } else if (healthInsuranceId > 0) {
          this.locationAutocomplete.setData(locationList);
          this.setLocationparams(healthInsuranceId);
        }
      }
    })
  }

  private controlLocationStepShow(quantityRecords: number): void {
    const hiddenSteps = this.wizard.getHiddenSteps();
    const isLocationHidden = hiddenSteps.includes(WizardAppointmentsIdentifies.LOCATION);
    if (quantityRecords <= 0 && !isLocationHidden) {
      hiddenSteps.push(WizardAppointmentsIdentifies.LOCATION);
      this.wizard.setHiddenSteps(hiddenSteps);
    } else if (quantityRecords > 0 && isLocationHidden) {
      const index = hiddenSteps.indexOf(WizardAppointmentsIdentifies.LOCATION);
      if (index >= 0) {
        hiddenSteps.splice(index, 1);
        this.wizard.setHiddenSteps(hiddenSteps);
      }
    }
  }

  private showQuizStep(): void {
    const options = {
      headers: { accept: MediaType.TEXT_PLAIN }
    };

    HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.QUIZ_AVAILABLE, {}, options).then(response => {
      this.isQuizAvailable = response.body?.value === Option.YES;
      if(!this.isQuizAvailable && !this.isReschedule) {
        const hiddenSteps = this.wizard.getHiddenSteps();
        hiddenSteps.push(WizardAppointmentsIdentifies.QUIZ);
        this.wizard.setHiddenSteps(hiddenSteps);
      }
    });
  }

  getTasyParams(): void {
    HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.TASY_INSURANCE_FIELD_PARAMETER).then((item) => {
      this.context.set({ tasyInsuranceParameter: item.body?.value });
    })
    this.getShowMedicalAreaValueFromTasy();
  }

  getShowMedicalAreaValueFromTasy(): Promise<void> {
    const currentFieldMedicalAreaIsVisible = this.context.get('fieldMedicalAreaIsVisible');
    if(currentFieldMedicalAreaIsVisible != null) {
      return Promise.resolve();
    }
    return HttpService.get<SimpleObjectResponse>(APPLICATION_ENDPOINTS.TASY_MEDICAL_AREA_PARAMETER).then((item) => {
      const tasyMedicalAreaParameter = item.body?.value;
      if (!tasyMedicalAreaParameter || tasyMedicalAreaParameter === 'N') {
        this.context.set({ fieldMedicalAreaIsVisible: false });
      } else {
        this.context.set({ fieldMedicalAreaIsVisible: true });
      }
    })
  }

  checkStepOne(appointmentsType: DefaultAllowedScheduling[]): void {
    const isAppointmentTypeFilled = this.checkAppointmentTypeFilled(appointmentsType);

    if (!isAppointmentTypeFilled) {
      this.wizard.setStepDone('schedule-type', false);
      return;
    }

    this.wizard.setStepDone('schedule-type', true);
  }

  checkStepTwo(): void {
    const isPrivateHealthInsurance = this.context.get('isPrivateHealthInsurance');
    const isEligibleInsurance = this.context.get('isEligibleInsurance');
    const isShowEligible = this.context.get('isShowEligible');

    if (isPrivateHealthInsurance) {
      this.wizard.setStepDone('scheduling-for', true);
      return;
    }

    if (this.panelInfo.isVisible() && isEligibleInsurance && !isShowEligible) {
      this.wizard.setStepDone('scheduling-for', true);
      return;
    }

    if (isEligibleInsurance) {
      this.wizardIdentifierToBeDoneWhenInsuranceFieldsFilled = 'scheduling-for';
      return;
    }

    if (this.panelInfo.isVisible() && this.segmentedControlInsurance.getValue() && this.segmentedControlInsurance.getValue().length > 0 && isEligibleInsurance) {
      this.wizard.setStepDone('scheduling-for', true);
      return;
    }

    this.wizard.setStepDone('scheduling-for', false);
  }

  setInsurenceStepFields(): void {
    const { nmPessoaFisica, cdPessoaFisica }: Patient = cast(this.patientAutocomplete.getValue());
    const formData = this.formInsurance.getRecord() as unknown as FormInsuranceRecordProps;
    const dontHasInsurance = formData.itemSelected[0].isPrivate;
    const autorizationCode = formData.autorizationCode || '';
    const insuranceSelected = this.context.get('insuranceSelected') as HealthInsuranceCardProps;
    this.newScheduleFields = this.newScheduleFields || {};
    this.newScheduleFields.patient = {
      nmPessoaFisica: nmPessoaFisica as string,
      patientId: cdPessoaFisica as string
    }
    if (dontHasInsurance) {
      this.newScheduleFields.insurance = {
        autorizationCode: '',
        category: {
          cdCategoria: this.privateCategoryCode,
          dsCategoria: '',
        },
        convenio: {
          cdConvenio: this.cdConvenioParticular,
          dsConvenio: '',
        },
        hasInsurance: false,
        healthCardNumber: insuranceSelected.healthCardNumber,
        healthCardValidity: insuranceSelected.walletExpiryDate,
        plan: {
          cdPlano: '',
          dsPlano: ''
        },
      }
    } else {
      const {
        healthInsuranceId: cdConvenio,
        healthInsuranceDesc: dsConvenio,
        categoryId: cdCategoria = '',
        categoryDesc: dsCategoria = '',
        planId: cdPlano = '',
        planDesc: dsPlano = '',
      } = this.getInsuranceCardSelectedData() || {};
      this.newScheduleFields.insurance = {
        autorizationCode: autorizationCode as string,
        category: {
          cdCategoria: cdCategoria as string,
          dsCategoria: dsCategoria as string,
        },
        convenio: {
          cdConvenio: (cdConvenio || '') as string,
          dsConvenio: (dsConvenio || '') as string,
        },
        hasInsurance: true,
        healthCardNumber: insuranceSelected.healthCardNumber,
        healthCardValidity: insuranceSelected.walletExpiryDate,
        plan: {
          cdPlano: (cdPlano || '') as string,
          dsPlano: (dsPlano || '') as string
        },
      }
    }
  }

  setScheduleTypeFields(): void {
    const appointmentsType = this.staticSegmentedControlAPI.getValue() || [];
    appointmentsType.forEach(appointmentType => {
      if (appointmentsType.length === 1) {
        if (appointmentType.value === 'schedule-type.exam') {
          this.setExamFields();
        } else {
          this.setConsultationFields();
        }
      } else {
        this.setConsultationFields();
        this.setExamFields();
      }
    });
  }

  setConsultationFields(): void {
    const fieldListItem = this.fieldSchedulingList.getValue() as ConsultFieldItem[];
    if (fieldListItem.length > 0) {
      this.newScheduleFields.consultation = [] as ConsultFieldItem[];
      fieldListItem.forEach(consult => {
        const { specialityData, medicalArea, physician } = consult;
        this.newScheduleFields.consultation.push({ medicalArea, physician, specialityData });
      })
    }
  }

  setExamFields(): void {
    const fieldListItem = this.fieldExamToSchedule.getValue() as ExamFieldItem[];
    this.newScheduleFields.exam = [] as ExamSchedule[];
    fieldListItem.forEach(item => {
      const exam = item.exam;
      if (exam && exam.dsProcInterno) {
        this.newScheduleFields.exam.push(exam);
      }
    })
  }

  setLocationFields(): void {
    const location = !this.wizard.getHiddenSteps().includes('location') && this.locationAutocomplete.getValue()
      ? this.locationAutocomplete.getValue() as unknown as LocationData
      : { dsGrupoEstab: '', nrSequencia: ''};
    if (this.haveLocation && location) {
      this.newScheduleFields.location = this.newScheduleFields.location || {};
      this.newScheduleFields.location.value = location.dsGrupoEstab as string;
      this.newScheduleFields.location.id = location.nrSequencia as string;
    }
  }

  checkAppointmentTypeFilled(appointmentsType: DefaultAllowedScheduling[]): boolean {
    if(!this.panelScheduleTypeAPI.isVisible()) {
      return false;
    }

    let hiddenSteps = this.wizard.getHiddenSteps();
    hiddenSteps = hiddenSteps.filter((step: string) => {
      return step === WizardAppointmentsIdentifies.LOCATION || step === WizardAppointmentsIdentifies.QUIZ;
    });
    if (appointmentsType.length > 0) {
      appointmentsType.forEach(appointmentType => {
        if (appointmentsType.length === 1) {
          if (appointmentType.value === 'schedule-type.exam') {
            hiddenSteps.push('consultation');
          } else {
            hiddenSteps.push('exams', 'exam-additional-information');
          }
        }
      });

      this.wizard.setHiddenSteps(hiddenSteps);
      return true;
    }
    return false;
  }

  checkInsuranceFieldsFilled = (): boolean => {
    const insuranceCardSelected = this.getInsuranceCardSelectedData();
    const insuranceCompleted = insuranceCardSelected || false;
    return !!insuranceCompleted;
  }

  showMedicalArea(cdEspecialidade: string): void {
    setTimeout(() => {
      this.medicalAreaFetch.setParameters({ cdEspecialidade: cdEspecialidade });
      this.medicalAreaFetch.forceFetch();
    });
  }

  @OnMount({target: 'segmented-control-0'})
  handleLoadSegmentedControl(): void {
    this.suggestionList = [];
    const isEligibilityCheckRendering = this.context.get('isEligibilityCheckRendering');

    if(isEligibilityCheckRendering) {
      this.context.set({isEligibilityCheckRendering: false});
      const previousInsuranceCards = this.context.get('insuranceCardsBeforeEligibilityVerification') as HealthInsuranceCardProps[];
      if(previousInsuranceCards != null && previousInsuranceCards.length > 0) {
        const previousInsurancesWithNewAdded = this.context.get('previousInsurancesWithNewAdded') as HealthInsuranceCardProps;
        if(previousInsurancesWithNewAdded != null) {
          this.segmentedControlInsurance.setData(previousInsurancesWithNewAdded as unknown as Record<string, Value>[]);
          this.context.set({previousInsurancesWithNewAdded: undefined});
        } else {
          this.segmentedControlInsurance.setData(previousInsuranceCards as unknown as Record<string, Value>[]);
        }
      }
    } else {
      const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;
      const patitientIdForLoad = currentPatientSelected ? currentPatientSelected.id : UserContext.patientId;
      this.loadPatientHealthInsurancesAndShow(patitientIdForLoad);
    }

    this.context.set({isAlreadyHealthInsurance: true});
    this.selectNewInsuranceDataAfterCheckEligibility();
    this.defineStepDoneWhenInsuranceFieldsFilled();
  }

  private selectNewInsuranceDataAfterCheckEligibility(): void {
    if (this.newInsuranceSelectedAfterCheckEligibility) {
      this.segmentedControlInsurance.setValue([{...this.newInsuranceSelectedAfterCheckEligibility}]);
    }
  }

  private defineStepDoneWhenInsuranceFieldsFilled(): void {
    const wizardStepIdentifier = this.wizardIdentifierToBeDoneWhenInsuranceFieldsFilled;
    if (!this.wizardIdentifierToBeDoneWhenInsuranceFieldsFilled) {
      return;
    }

    if (wizardStepIdentifier && this.checkInsuranceFieldsFilled()) {
      this.wizard.setStepDone(wizardStepIdentifier, true);

      // Remove prop value.
      this.wizardIdentifierToBeDoneWhenInsuranceFieldsFilled = undefined;
    }
  }

  @OnUnmount({target: 'segmented-control-0'})
  handleUnMountSegmentedControlInsurance(): void {
    this.context.set({isAlreadyHealthInsurance: false});
  }

  private async setHealthInsurancePrivate(): Promise<PrivateInsuranceConfiguration> {
    return HttpService.get(APPLICATION_ENDPOINTS.PARTICULAR_INSURANCE, {})
      .then(response => response.body as PrivateInsuranceConfiguration);
  }

  private async loadPatientHealthInsurancesAndShow(selectedPatientId: number): Promise<void> {
    try {
      const response = await this.setHealthInsurancePrivate();
      this.cdConvenioParticular = response.insuranceCode.toString();
      this.privateCategoryCode = response.categoryCode;

      const myInsuranceResponse = await HttpService.get(APPLICATION_ENDPOINTS.GET_MY_INSURANCES_DATA_CARD, {patientId: selectedPatientId});
      const data = myInsuranceResponse.body as HealthInsuranceCardProps[];
      const { newItemButtonId, privateButtonId } = this.HealthInsuranceCardDefaultIds;
      const lastDefaultItems = [
        {cardId: privateButtonId, healthInsuranceId: this.cdConvenioParticular , isPrivate: true},
        {cardId: newItemButtonId, isNewItemButton: true}
      ] as unknown as HealthInsuranceCardProps[];
      data.push(...lastDefaultItems);
      if (typeof this.segmentedControlInsurance.setData === 'function') {
        this.segmentedControlInsurance.setData(data as unknown as Record<string, Value>[]);
      } else {
        this.historyService.push(APPLICATION_ROUTES.APPOINTMENTS, {}, {scrollToTop: true});
      }
    } catch {
      this.callToastLoadDataError();
    }
  }

  @OnChange({target: AppointmentsAliases.SEGMENTED_CONTROL_0})
  handleChangeSegmentedControl(event: ChangeEvent<HealthInsuranceCardProps[]>): void {
    const selectedValue = event.value?.length === 1 ? event.value[0] : undefined as unknown as HealthInsuranceCardProps;
    const { privateButtonId, newItemButtonId } = this.HealthInsuranceCardDefaultIds;
    const isNewItemButton = selectedValue.cardId === newItemButtonId;

    this.clearMedicalFields();

    if(selectedValue) {
      this.context.set({
        cdConvenio: selectedValue.healthInsuranceId,
        ieInsurancePrivate: selectedValue.isPrivate,
        isPrivateHealthInsurance: selectedValue && selectedValue.cardId === privateButtonId,
      });

      if(selectedValue.isNewItemButton) {
        ModalService.openFullscreen({
          closeable: true,
          contextData: {tasyInsuranceParameter: this.context.get('tasyInsuranceParameter')},
          dockButtons: [],
          fragment: 'insurance-autocomplete-modal',
          onClose: () => this.handleCloseInsuranceAutocompleteModal(),
          onSubmit: (eventSubmit: SubmitEvent) => this.handleSubmitInsuranceAutocompleteModal(eventSubmit),
          title: MessageService.translate('tws.label.add-insurance'),
        });
      }

      if(selectedValue.cardId !== privateButtonId && !isNewItemButton) {
        const {
          panelInfo,
          panelLoaderEligibilityAPI,
          patientAutocomplete,
          context
        } = this;

        const insuranceSelected = context.get('insuranceSelected') as HealthInsuranceCardProps;
        if(insuranceSelected?.cardId === selectedValue.cardId && insuranceSelected.cardId) {
          return;
        }
        context.set({
          insuranceCardsBeforeEligibilityVerification: this.segmentedControlInsurance.getData(),
          insuranceSelected: selectedValue,
          isEligibilityCheckRendering: true,
          scheduleTypeSelected: this.staticSegmentedControlAPI.getValue() || [],
        });
        panelInfo.setVisible(false);
        patientAutocomplete.setEnabled(false);
        panelLoaderEligibilityAPI.setVisible(true);
        context.set({isShowEligible: true});
        this.checkHealthInsuranceEligibility(selectedValue as HealthInsuranceCardProps)
          .then(response => {
            const { eligibility } = response.body as EligibilityInsuranceResponse;
            panelInfo.setVisible(true);
            panelLoaderEligibilityAPI.setVisible(false);
            this.newInsuranceSelectedAfterCheckEligibility = selectedValue;
            if(!eligibility.eligible) {
              showToastNotificationWarning(MessageService.translate('tws.text.eligibility-insurance.denial'));
              context.set({isEligibleInsurance: false});
              return;
            }
            context.set({isEligibleInsurance: true});
          })
          .catch(({ response }: HttpServiceError<EligibilityResponseError>) => {
            panelInfo.setVisible(true);
            panelLoaderEligibilityAPI.setVisible(false);
            context.set({insuranceSelected: undefined, isEligibleInsurance: true, isShowEligible: response?.body});
            const responseError = response?.body;
            if(responseError && !responseError.proceedIfHasError) {
              context.set({isEligibleInsurance: false});
              showToastNotificationWarning(responseError.message);
              return;
            }
            context.set({insuranceSelected: selectedValue});
          })
          .finally(() => {
            patientAutocomplete.setEnabled(true);
            const scheduleTypeSelected = context.get('scheduleTypeSelected') as Record<string, Value>[];
            if(scheduleTypeSelected.length > 0) {
              this.staticSegmentedControlAPI.setValue([...scheduleTypeSelected]);
            }
            this.checkStepTwo();
          });
      } else {
        this.context.set({insuranceSelected: selectedValue});
      }

      if (!isNewItemButton) {
        AppointmentService.messageOnInsuranceSelection(selectedValue);
      }

      this.showLocationStep(selectedValue.healthInsuranceId);
    } else {
      this.context.set({
        insuranceSelected: undefined,
        isEligibleInsurance: false,
        isPrivateHealthInsurance: false,
      });
      this.checkStepTwo();
    }

    if(!this.context.get('isEligibilityCheckRendering')) {
      this.checkStepTwo();
    }
  }

  async checkHealthInsuranceEligibility(healthInsuranceCard: HealthInsuranceCardProps): Promise<HttpResponse<EligibilityInsuranceResponse>> {
    const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;

    const params: EligibilityDTO = {
      beneficiary: healthInsuranceCard.healthCardNumber,
      cardExpirationDate: healthInsuranceCard.walletExpiryDate,
      categoryCode: healthInsuranceCard.categoryId,
      insuranceCode: healthInsuranceCard.healthInsuranceId,
      naturalPersonName: currentPatientSelected.name,
      planCode: healthInsuranceCard.planId,
    };

    return PatientHttpService.post(APPLICATION_ENDPOINTS.CHECK_INSURANCE_ELIGIBILITY, {}, { body: {...params}});
  }

  handleSubmitInsuranceAutocompleteModal(event: SubmitEvent): void {
    const record = event.record as HealthInsuranceModalSubmitedProps;
    if(Object.entries(record).length === 0) {
      return;
    }

    const { privateButtonId, newItemButtonId } = this.HealthInsuranceCardDefaultIds;
    const currentInsurances = this.segmentedControlInsurance.getData() as unknown as HealthInsuranceCardProps[];
    const healthInsurancesReduced = currentInsurances.reduce<HealthInsurancesReduced>((insurances, item) => {
      if(item.cardId !== privateButtonId && item.cardId !== newItemButtonId) {
        insurances.insurancesCards.push(item)
      } else {
        insurances.insurancesCardButtons.push(item);
      }
      return insurances;
    }, {insurancesCardButtons: [], insurancesCards: []});

    const newInsuranceItem = {
      cardId: generateUUID(),
      categoryDesc: record.category?.dsCategoria,
      categoryId: record.category?.cdCategoria,
      healthCardNumber: record.healthCardNumber,
      healthInsuranceDesc: record.insurance.dsConvenio,
      healthInsuranceId: record.insurance.cdConvenio,
      planDesc: record.plan?.dsPlano,
      planId: record.plan?.cdPlano,
      walletExpiryDate: record.walletExpiryDate,
    } as unknown as HealthInsuranceCardProps;

    const newInsurances = healthInsurancesReduced.insurancesCards;
    newInsurances.push(newInsuranceItem);
    newInsurances.push(...healthInsurancesReduced.insurancesCardButtons);
    this.context.set({previousInsurancesWithNewAdded: newInsurances});

    this.segmentedControlInsurance.setData(newInsurances as unknown as Record<string, Value>[]);
    this.segmentedControlInsurance.setValue([{...newInsuranceItem}]);

    if(record.saveInsurance) {
      const insurance = {
        cdCategoria: newInsuranceItem.categoryId,
        cdConvenio: newInsuranceItem.healthInsuranceId,
        cdPlanoConvenio: newInsuranceItem.planId,
        cdUsuarioConvenio: newInsuranceItem.healthCardNumber,
        dtValidadeCarteira: newInsuranceItem.walletExpiryDate,
      } as HealthInsuranceHolderDTO;

      this.saveInsurance(insurance);
    }
  }

  handleCloseInsuranceAutocompleteModal(): void {
    this.segmentedControlInsurance.setValue([]);
  }

  private getInsuranceCardSelectedData(): HealthInsuranceCardProps | undefined {
    const data = this.segmentedControlInsurance.getValue() || [];
    return data.length === 0 ? undefined : data[0] as unknown as HealthInsuranceCardProps;
  }

  private saveInsurance(insurance: HealthInsuranceHolderDTO): void {
    const body = [insurance] as unknown as Record<string, Value>;
    const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;

    HttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_CREATE_INSURANCE, {patientId: currentPatientSelected.id}, { body })
      .then(() => showToastNotificationSuccess(MessageService.translate('insurance.new-insurance-added')))
      .catch((error) => showToastNotificationWarning(error?.message));
  }

  @OnMount({target: 'suggestion-card-item-0'})
  handleSuggestionCardLoading(event: MountEvent): void {
    const suggestionCard = event.target as SuggestionCardItemAPI;
    suggestionCard.setFunctionToCallWaitingListModal(this.showWaitingListModal.bind(this));
    suggestionCard.setFunctionToRemoveItemFromWaitingListModal(this.removeWaitingListItemToContext.bind(this));
  }

  private showWaitingListModal = (waitingListDataFromCard?: WaitingListDataFromCardProps): void => {
    const selectedPeriod = this.newScheduleFields?.dataRage;
    let availablePeriod: string[] = [];
    if(selectedPeriod?.start) {
      availablePeriod = [moment.utc(selectedPeriod.start).format('YYYY-MM-DD'), moment.utc(selectedPeriod.end).format('YYYY-MM-DD')];
    }

    const waitingListDataTemp = this.context.get('waitingListData') as WaitingListData;
    const waitingListData = waitingListDataFromCard
      ? {...waitingListDataTemp, ...waitingListDataFromCard, availablePeriod }
      : waitingListDataTemp;

    const haveToReturnWaitingListItem = !!waitingListDataFromCard;
    const modalProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { haveToReturnWaitingListItem, waitingListData },
      focusOnOpen: true,
      fragment: 'scheduling-waiting-list',
      noPadding: true,
      onSubmit: event => this.handleSubmitWaitingListModalByCard(event),
      title: MessageService.translate('patient.label.modal-scheduling-waiting-list'),
      uuid: generateUUID(),
    }

    isMobile() ? ModalService.openFullscreen(modalProps): ModalService.openDialog(modalProps);
  }

  private removeWaitingListItemToContext(nrSeqAgeintItem: string): void {
    const waitingListArrayToPersistenceFromContext = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    const waitingListReduced = waitingListArrayToPersistenceFromContext.reduce<WaitingListDataReduced>((waitingList, item) => {
      if(item.nrSeqAgeintItem === nrSeqAgeintItem) {
        waitingList.itemRemoved = item;
      } else {
        waitingList.newData = [...waitingList.newData, item];
      }
      return waitingList;
    }, { newData: []});

    const doesNotExistsSuggestions = this.context.get('doesNotExistsSuggestions') as boolean;
    const allSuggestionsHaveBeenSelected = this.context.get('allSuggestionsHaveBeenSelected') as boolean;
    const enableConfirmButton = allSuggestionsHaveBeenSelected || (waitingListReduced.newData.length > 0 && doesNotExistsSuggestions);
    this.wizard.setStepDone('suggestion', enableConfirmButton);
    if(!waitingListReduced.itemRemoved) {
      return;
    }

    this.context.set({ waitingListArrayToPersistence: waitingListReduced.newData });

    const deckDataTemp = this.deckChoosenItems.getData();
    const deckData = deckDataTemp as unknown as ScheduleItem[];

    const newDeckData: ScheduleItem[] = deckData.map<ScheduleItem>(item => {
      return item.nrSeqAgeintItem === waitingListReduced.itemRemoved?.nrSeqAgeintItem
        ? { ...item, isWaitingListItem: false }
        : item;
    });

    this.deckChoosenItems.setData(newDeckData as unknown as Record<string, Value>[]);
    this.deckChoosenItems.setDone(true);
  }

  private handleSubmitWaitingListModalByCard(event: SubmitEvent): void {
    const waitingListArrayToPersistenceFromContext = this.context.get('waitingListArrayToPersistence') as ScheduleWaitingListData[] || [];
    const waitingListItemToPersistenceFromModal = event.record as ScheduleWaitingListData;
    const waitingListArrayToPersistence: ScheduleWaitingListData[] = [
      ...waitingListArrayToPersistenceFromContext,
      waitingListItemToPersistenceFromModal
    ];
    this.context.set({ waitingListArrayToPersistence });
    const doesNotExistsSuggestions = this.context.get('doesNotExistsSuggestions') as boolean;
    const allSuggestionsHaveBeenSelected = this.context.get('allSuggestionsHaveBeenSelected') as boolean;
    const enableConfirmButton = allSuggestionsHaveBeenSelected || (waitingListArrayToPersistence.length > 0 && doesNotExistsSuggestions);
    this.wizard.setStepDone('suggestion', enableConfirmButton);
    this.handleAfterSubmitWaitingListModalByCard(waitingListItemToPersistenceFromModal);
  }

  private handleAfterSubmitWaitingListModalByCard(waitingListItemFromModal?: ScheduleWaitingListData): void {
    if(!waitingListItemFromModal) {
      return ;
    }

    const deckDataTemp = this.deckChoosenItems.getData();
    const deckData = deckDataTemp as unknown as ScheduleItem[];

    const newDeckData: ScheduleItem[] = deckData.map<ScheduleItem>(item => {
      return waitingListItemFromModal.nrSeqAgeintItem === item.nrSeqAgeintItem
        ? { ...item, isWaitingListItem: true }
        : { ...item, isWaitingListItem: !!item.isWaitingListItem };
    });
    this.deckChoosenItems.setData(newDeckData as unknown as Record<string, Value>[]);
    this.deckChoosenItems.setDone(true);
  }

  @OnClick({target: 'link-call-waiting-list'})
  handleButtonWaitingModalClick(): void {
    this.showWaitingListModal();
  }

  private isStepShowing(identifier: string): boolean {
    return !this.wizard.getHiddenSteps().includes(identifier);
  }

  private setWaitingListDataToContext(): void {
    // nrSeqAgeint is initially 0, but its value is updated in context during steps.
    // Default values.
    const waitingListData: WaitingListData = {
      availablePeriod: [],
      consultScheduleList: [],
      dependencies: {
        calendarFinalDateInitialRange: this.state.finalDateInitialRange,
        currentPatientSelected: this.context.get<CurrentPatientSelected>('currentPatientSelected'),
        healthInsuranceCategoryId: '',
        healthInsuranceId: cast(this.cdConvenioParticular),
        healthInsurancePlanId: '',
      },
      examScheduleList: [],
      medicalSpecialities: cast([{}]),
      nrSeqAgeint: 0,
      nrSeqAgeintItem: '0',
      physician: cast({}),
      scheduleType: [],
    };

    if (this.isStepShowing('scheduling-for')) {
      waitingListData.scheduleType = cast(defaultIfUndefined(this.staticSegmentedControlAPI.getValue(), []));
      waitingListData.dependencies.healthInsuranceId = cast(defaultIfUndefined(this.getInsuranceCardSelectedData()?.healthInsuranceId, this.cdConvenioParticular));
      waitingListData.dependencies.healthInsuranceCategoryId = cast(defaultIfUndefined(this.getInsuranceCardSelectedData()?.categoryId, ''));
      waitingListData.dependencies.healthInsurancePlanId =  cast(defaultIfUndefined(this.getInsuranceCardSelectedData()?.planId, ''));
    }

    if (this.isStepShowing('exams')) {
      waitingListData.examScheduleList = cast(defaultIfUndefined(this.fieldExamToSchedule.getValue(), []));
    }

    if (this.isStepShowing('consultation')) {
      waitingListData.consultScheduleList = cast(defaultIfUndefined(this.fieldSchedulingList.getValue(), []));
      waitingListData.medicalSpecialities = cast(defaultIfUndefined(this.specialityFields.map(field => field.getValue() || {}), [{}]));
      waitingListData.physician = cast({});
    }

    this.context.set({ waitingListData });
  }

  @OnValidate({ target: 'form-additional-information'})
  handleFormAdditionalInformationValidate(event: ValidateEvent): void {
    this.wizard.setStepDone('exam-additional-information', event.valid);
  }

  @OnBeforeFetch({ target: 'fetch-additional-information' })
  handleFetchAdditionalInformationBeforeGet(event: AbortableFetchEvent): void {
    const currentPatientSelected = this.context.get('currentPatientSelected') as CurrentPatientSelected;
    if(!currentPatientSelected || currentPatientSelected && currentPatientSelected.id === 0) {
      event.abort();
    }
  }

  @OnAfterFetch({ target: 'fetch-additional-information' })
  handleFetchAdditionalInformationAfterGet(event: FetchEvent): void {
    this.formAdditionalInformationAPI.setLoading(false);

    const data = event.data;
    if(!event.error && data && Object.keys(data).length > 1) {
      this.formAdditionalInformationAPI.validate();
      this.wizard.setStepDone('exam-additional-information', true);
    }
  }

  /** Begin - handle steps result */
  @OnMount({target: 'form-patient-result'})
  handleFormPatientResult(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    formResult.setValue(this.formPatient.getRecord());
  }

  @OnMount({target: 'form-insurance-result'})
  handleFormInsuranceResult(event: MountEvent): void {
    const insuranceCardsData = this.segmentedControlInsurance.getData();
    this.segmentedControlInsuranceResult.setData(insuranceCardsData);

    const formResult = event.target as FormAPI;
    formResult.setValue(this.formInsurance.getRecord());
  }

  @OnMount({target: 'form-schedule-type-result'})
  handleFormScheduleTypeResult(event: MountEvent): void {
    const scheduleTypeCardsData = this.staticSegmentedControlAPI.getData();
    this.staticSegmentedControlResultAPI.setData(scheduleTypeCardsData);

    const formResult = event.target as FormAPI;
    formResult.setValue(this.formScheduleTypeAPI.getRecord());
  }

  @OnMount({ target: 'form-additional-information-result'})
  handleFormAdditionalInformationResultLoading(event: MountEvent): void {
    const formAPI = event.target as FormAPI;
    formAPI.setValue(this.formAdditionalInformationAPI.getRecord());
  }

  @OnMount({target: 'step-2-form-result'})
  handleStep2FormResult(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    formResult.setValue(this.step2FormAPI.getRecord());
  }

  @OnChange({target: AppointmentsAliases.STEP_2_FIELD})
  onChangeLocationStep(event: ChangeEvent<EstablishmentGroupLocalizationModel>): void {
    const applicationContext = getFoundationDataContext();
    const isLocationRequired = applicationContext.get(REQUIREMENT_FIELDS.LOCATION_REQUIRED);
    if (isLocationRequired) {
      const isLocationStepDone = event.value != undefined;
      this.wizard.setStepDone(WizardAppointmentsIdentifies.LOCATION, isLocationStepDone);
      applicationContext.set({ isLocationStepDone });
    }
  }

  @OnChange({target: AppointmentsAliases.AUTOCOMPLETE_LOCATION})
  onChangeLocationValue(): void {
    this.clearMedicalFields();
  }

  clearMedicalFields(): void {
    if (this.context.get('scheduleLength') === 2) {
      if (this.fieldSpeciality.getValue() || this.fieldPhysician.getValue() || this.fieldExamToSchedule.getValue()) {
        this.fieldSpeciality.setValue(undefined);
        this.fieldPhysician.setValue(undefined);
      }
    } else if (this.context.get('scheduleLength') === 1) {
      if (this.context.get('isOnlyConsultation')) {
        if (this.fieldSpeciality.getValue() || this.fieldPhysician.getValue()) {
          this.fieldSpeciality.setValue(undefined);
          this.fieldPhysician.setValue(undefined);
        }
      } else if (this.fieldExamToSchedule.getValue()) {
        this.fieldExamToSchedule.setValue(undefined);
      }
    }
  }

  @OnMount({target: AppointmentsAliases.STEP_2_FIELD})
  handleLocationFieldStep(event: MountEvent): void {
    const locationField = event.target as FieldAPI;
    const applicationContext = getFoundationDataContext();
    AppointmentService.getAppointmentFieldLocation(locationField).then(isLocationRequired => {
      applicationContext.set({ isLocationRequired });
    });
  }

  @OnMount({target: 'form-step-3-result'})
  handleStep3Result(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    formResult.setValue(this.formStep3API.getRecord());
  }

  @OnMount({target: 'form-exams-to-schedule-result'})
  handleFormExamsToScheduleResult(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    formResult.setValue(this.formExamsToScheduleAPI.getRecord());
  }

  @OnMount({target: 'form-datefield-result'})
  handleDatePickerAppointmentRangeResult(event: MountEvent): void {
    const formResult = event.target as FormAPI;
    const startAt = moment.utc(this.selectedDateRange.start);
    const endAt = moment.utc(this.selectedDateRange.end);
    formResult.setValue({selectedPeriodDatePicker: [startAt, endAt]});
  }

  async onNavigateToConsultation(): Promise<void> {
    const isConsultationFieldsValid = await this.isValidFieldsForConsultation();
    const isFormValid = await this.formStep3API.validate();
    this.wizard.setStepDone(WizardAppointmentsIdentifies.CONSULTATION, isConsultationFieldsValid && isFormValid);
  }

  async onNavigateToExams(): Promise<void> {
    const exams = this.fieldExamToSchedule.getValue() as ExamFieldItem[];
    const isExamFilled = exams.every(item => item.exam);
    const isFormValid = await this.formExamsToScheduleAPI.validate();
    this.wizard.setStepDone(WizardAppointmentsIdentifies.EXAMS, isExamFilled && isFormValid);
  }

  async onNavigateToExamAdditionalInformation(): Promise<void> {
    const isFormValid = await this.formAdditionalInformationAPI.validate();
    this.wizard.setStepDone(WizardAppointmentsIdentifies.EXAM_ADDITIONAL_INFORMATION, isFormValid);
  }

  async onNavigateToLocation(from: string): Promise<void> {
    const applicationContext = getFoundationDataContext();
    const isLocationRequired = applicationContext.get(REQUIREMENT_FIELDS.LOCATION_REQUIRED);
    const isLocationStepDone = applicationContext.get(REQUIREMENT_FIELDS.LOCATION_STEP_DONE);
    const isAvailableLocation = this.haveLocation;
    this.wizard.setStepDone(WizardAppointmentsIdentifies.LOCATION, (isLocationStepDone || !isLocationRequired));
    if (!isAvailableLocation && (from == WizardAppointmentsIdentifies.CONSULTATION || from == WizardAppointmentsIdentifies.EXAMS)) {
      this.wizard.prevStep();
    } else if (!isAvailableLocation) {
      this.wizard.nextStep();
    }
  }

  setLocationparams(insuranceCode: number): void {
    this.fetchLocationList.setParameters({ cdConvenio: insuranceCode });
    this.fetchLocationList.forceFetch();
  }
  /** End - handle steps result */
}
